import React, { useState, useEffect, useMemo } from 'react';
import { BarChart3, Clock, Layers, X, Download, Users, UserPlus, Image, Video, UserCheck, Mail, Zap, Settings, Sparkles, Activity, MessageSquare, Send, RefreshCw, Search, MessageCircle, BadgeCheck, Shield, Headphones, Loader2, Plus, Trash2, Edit, FileText, Briefcase, Star, ChevronLeft, ChevronRight, CreditCard } from 'lucide-react';
import {
  getPersonaLabel,
  normalizePersonasList,
  USER_PERSONAS,
  USER_PERSONA_LABELS,
} from '../constants/userPersonas';
import {
  RUNNING_PLAN_IDS,
  RUNNING_PLAN_LABELS,
  formatPlanExpiryDate,
  isPaidPlanActive,
  normalizeRunningPlanId,
  runningPlanLabel,
} from '../constants/runningPlans';
import { getKits, API_BASE_URL, getAccessRequests, updateAccessRequestStatus, deleteAccessRequest, updateVirtualTryOnSetting, updateVendorVerified, resendWelcomeEmail, createPublicCatalogue, getFabricMaterialStatsToday, getAICreationsStatsToday, getDailyActiveUsers, broadcastNotification, sendNotificationToUsers, getSupportRequests, updateSupportRequest, getAllFeedback, getWhatsAppStatus, getWhatsAppTemplates, createWhatsAppTemplate, updateWhatsAppTemplate, deleteWhatsAppTemplate, getWhatsAppBroadcasts, broadcastWhatsApp, sendWhatsAppToUsers, getAppSettings, updateNanoBananaProvider } from '../api';
import { updateKit } from '../api/kit.api';
import { getFabricVendorConnectRequests } from '../api/fabricVendorConnectRequest.api';
import JSZip from 'jszip';

/** Format an ISO date for `<input type="date" />` (yyyy-mm-dd). */
function toDateInputValue(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Convert date input value to end-of-day local ISO for storage. */
function dateInputToExpiryIso(dateStr) {
  if (!dateStr || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return null;
  const [y, m, d] = dateStr.split('-').map((n) => parseInt(n, 10));
  const expires = new Date(y, m - 1, d, 23, 59, 59, 999);
  if (Number.isNaN(expires.getTime())) return null;
  return expires.toISOString();
}

const UserPersonaBadges = ({ personas, className = '' }) => {
  const normalized = normalizePersonasList(personas);
  if (!normalized.length) {
    return <span className={`text-xs italic text-gray-400 ${className}`}>Not set</span>;
  }

  return (
    <div className={`flex flex-wrap gap-1 ${className}`}>
      {normalized.map((persona) => (
        <span
          key={persona}
          className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-indigo-50 text-indigo-800 border border-indigo-100"
          title={getPersonaLabel(persona)}
        >
          {getPersonaLabel(persona)}
        </span>
      ))}
    </div>
  );
};

const ApprovalModal = ({ kit, onClose, onApprove, showNotification }) => {
  const [formData, setFormData] = useState({
    name: kit?.name || '',
    brandName: kit?.brandProfile?.brandName || kit?.brandProfile?.name || '',
    productName: kit?.productDetails?.name || '',
    model: kit?.selectedModel || '',
    contentTypes: kit?.contentPreferences?.contentTypes || [],
    targetAudience: kit?.contentPreferences?.targetAudience || '',
    tone: kit?.contentPreferences?.tone || '',
    additionalNotes: '',
    brandLogo: kit?.brandProfile?.logo || '',
    productImage: kit?.productDetails?.image || '',
    modelImage: kit?.modelImage || '',
    sceneImage: kit?.sceneImage || '',
    imagePrompt: kit?.imagePrompt || '',
    scenePrompt: kit?.selectedTemplate?.prompt || '',
    generatedImageUrl: kit?.generatedImageUrl || '',
    videoUrl: kit?.videoUrl || ''
  });

  const handleDownloadAll = async () => {
    const zip = new JSZip();
    const images = [
      { url: formData.brandLogo, name: 'brand-logo' },
      { url: formData.productImage, name: 'product-image' },
      { url: formData.modelImage, name: 'model-image' },
      { url: formData.sceneImage, name: 'scene-image' }
    ];

    const failedImages = [];
    const downloadedImages = [];

    for (const img of images) {
      if (!img.url || typeof img.url !== 'string' || !img.url.startsWith('http')) {
        continue; // skip empty or invalid URLs
      }
      try {
        const response = await fetch(img.url, { mode: 'cors' });
        if (!response.ok) throw new Error('Network response was not ok');
        const blob = await response.blob();
        downloadedImages.push({ blob, name: img.name });
      } catch (err) {
        failedImages.push(img.name);
        console.error(`Failed to fetch image: ${img.name} (${img.url})`, err);
      }
    }

    if (downloadedImages.length === 0) {
      showNotification('No images could be downloaded. Please check the image URLs or CORS settings.', 'error');
      return;
    }

    // Add each image to the zip file
    downloadedImages.forEach(({ blob, name }) => {
      zip.file(`${name}.jpg`, blob);
    });

    // Generate and download the zip file
    const content = await zip.generateAsync({ type: 'blob' });
    const url = window.URL.createObjectURL(content);
    const link = document.createElement('a');
    link.href = url;
    link.download = `kit-images-${Date.now()}.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    if (failedImages.length > 0) {
      showNotification(`Some images could not be downloaded: ${failedImages.join(', ')}`, 'error');
    } else {
      showNotification('All images downloaded successfully!', 'success');
    }
  };

  const handleApprove = async (e) => {
    e.preventDefault();
    try {
      await onApprove(formData);
      showNotification('Kit approved successfully', 'success');
      onClose();
    } catch (error) {
      showNotification('Failed to approve kit', 'error');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const ImagePreview = ({ src, alt, title }) => {
    const handleDownload = async () => {
      if (!src) return;
      
      try {
        const response = await fetch(src);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${title.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error downloading image:', error);
      }
    };

    return (
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <p className="text-sm font-medium text-purple-700">{title}</p>
          {src && (
            <button
              onClick={handleDownload}
              className="text-pink-500 hover:text-pink-600 flex items-center gap-1 text-sm"
            >
              <Download size={16} />
              Download
            </button>
          )}
        </div>
        {src ? (
          <div className="relative aspect-square w-full overflow-hidden rounded-lg border border-purple-100 group">
            <img
              src={src}
              alt={alt}
              className="object-cover w-full h-full"
            />
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
              <button
                onClick={handleDownload}
                className="bg-white p-2 rounded-full shadow-lg hover:bg-pink-50 transition-colors"
              >
                <Download size={20} className="text-pink-500" />
              </button>
            </div>
          </div>
        ) : (
          <div className="aspect-square w-full rounded-lg border border-purple-100 bg-gray-50 flex items-center justify-center">
            <p className="text-gray-400 text-sm">No image available</p>
          </div>
        )}
        {src && (
          <a
            href={src}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-pink-500 hover:text-pink-600 block truncate"
          >
            {src}
          </a>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-purple-100">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-purple-700">Approve Kit Request</h2>
            <div className="flex items-center gap-4">
              <button
                onClick={handleDownloadAll}
                className="flex items-center gap-2 px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors"
              >
                <Download size={20} />
                Download All Images
              </button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>
          </div>
        </div>
        
        <form onSubmit={handleApprove} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-purple-700 mb-1">Kit Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-purple-700 mb-1">Brand Name</label>
              <input
                type="text"
                name="brandName"
                value={formData.brandName}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-purple-700 mb-1">Product Name</label>
              <input
                type="text"
                name="productName"
                value={formData.productName}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-purple-700 mb-1">Model</label>
              <input
                type="text"
                name="model"
                value={formData.model}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-purple-700 mb-1">Target Audience</label>
              <input
                type="text"
                name="targetAudience"
                value={formData.targetAudience}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-purple-700 mb-1">Tone</label>
              <input
                type="text"
                name="tone"
                value={formData.tone}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-purple-700 mb-1">Additional Notes</label>
            <textarea
              name="additionalNotes"
              value={formData.additionalNotes}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Any additional notes or instructions..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-purple-700 mb-1">Generated Image URL</label>
            <input
              type="text"
              name="generatedImageUrl"
              value={formData.generatedImageUrl}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="URL of the generated image"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-purple-700 mb-1">Video URL</label>
            <input
              type="text"
              name="videoUrl"
              value={formData.videoUrl}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="URL of the video (e.g., S3 video URL)"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ImagePreview src={formData.brandLogo} alt="Brand Logo" title="Brand Logo" />
            <ImagePreview src={formData.productImage} alt="Product Image" title="Product Image" />
            <ImagePreview src={formData.modelImage} alt="Model Image" title="Model Image" />
            <ImagePreview src={formData.sceneImage} alt="Scene Image" title="Scene Image" />
          </div>

          <div className="flex justify-end space-x-4 pt-6 border-t border-purple-100">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors"
            >
              Approve Kit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditKitModal = ({ kit, isOpen, onClose, onUpdate, showNotification }) => {
  if (!isOpen || !kit) return null;
  const [formData, setFormData] = useState({
    name: kit?.name || '',
    generatedImageUrl: kit?.generatedImageUrl || '',
    videoUrl: kit?.videoUrl || '',
    status: kit?.status || 'pending'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await onUpdate(kit._id, formData);
      showNotification('Kit updated successfully', 'success');
      onClose();
    } catch (error) {
      showNotification('Failed to update kit', 'error');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const ImagePreview = ({ src, alt, title }) => {
    if (!src) {
      return (
        <div className="space-y-2">
          <p className="text-sm font-medium text-purple-700">{title}</p>
          <div className="aspect-square w-full rounded-lg border border-purple-100 bg-gray-50 flex items-center justify-center">
            <p className="text-gray-400 text-sm">No image available</p>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-2">
        <p className="text-sm font-medium text-purple-700">{title}</p>
        <div className="relative aspect-square w-full overflow-hidden rounded-lg border border-purple-100">
          <img
            src={src}
            alt={alt}
            className="object-cover w-full h-full"
          />
        </div>
        <a
          href={src}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-pink-500 hover:text-pink-600 block truncate"
        >
          {src}
        </a>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-purple-100">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-purple-700">Edit Kit</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-purple-700 mb-1">Kit Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-purple-700 mb-1">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="pending">Pending</option>
                <option value="generated">Generated</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-purple-700 mb-1">Generated Image URL</label>
              <div className="flex gap-2">
                <input
                  type="url"
                  name="generatedImageUrl"
                  value={formData.generatedImageUrl}
                  onChange={handleChange}
                  placeholder="https://example.com/image.jpg"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, generatedImageUrl: '' }))}
                  className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Clear
                </button>
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-purple-700 mb-1">Video URL</label>
              <div className="flex gap-2">
                <input
                  type="url"
                  name="videoUrl"
                  value={formData.videoUrl}
                  onChange={handleChange}
                  placeholder="https://example.com/video.mp4"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, videoUrl: '' }))}
                  className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>

          {/* Image Previews */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ImagePreview 
              src={formData.generatedImageUrl} 
              alt="Generated Image" 
              title="Generated Image Preview" 
            />
            <div className="space-y-2">
              <p className="text-sm font-medium text-purple-700">Video Preview</p>
              {formData.videoUrl ? (
                <div className="aspect-video w-full rounded-lg border border-purple-100 overflow-hidden">
                  <video 
                    src={formData.videoUrl} 
                    controls 
                    className="w-full h-full object-cover"
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
              ) : (
                <div className="aspect-video w-full rounded-lg border border-purple-100 bg-gray-50 flex items-center justify-center">
                  <p className="text-gray-400 text-sm">No video available</p>
                </div>
              )}
              {formData.videoUrl && (
                <a
                  href={formData.videoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-pink-500 hover:text-pink-600 block truncate"
                >
                  {formData.videoUrl}
                </a>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-4 pt-6 border-t border-purple-100">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors"
            >
              Update Kit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const UserSettingsModal = ({ user, onClose, onUpdate, onExportContacts, isExportingContacts, showNotification }) => {
  const [role, setRole] = useState((user?.role || 'user').toLowerCase() === 'admin' ? 'admin' : 'user');
  const [runningPlan, setRunningPlan] = useState(
    normalizeRunningPlanId(user?.runningPlan) || 'free'
  );
  const [planExpiresAt, setPlanExpiresAt] = useState(() => toDateInputValue(user?.planExpiresAt));
  const [isInfluencerAgent, setIsInfluencerAgent] = useState(Boolean(user?.isInfluencerAgent));
  const [influencerDiscountPercent, setInfluencerDiscountPercent] = useState(
    user?.influencerDiscountPercent ?? 10
  );
  const [influencerPromoCode, setInfluencerPromoCode] = useState(user?.influencerPromoCode || '');
  const [formData, setFormData] = useState({
    virtualTryOnEnabled: user?.settings?.virtualTryOnEnabled || false,
    fourKEnabled: user?.settings?.fourKEnabled || false,
    notifications: {
      contentReady: user?.settings?.notifications?.contentReady ?? true,
      promotionalEmails: user?.settings?.notifications?.promotionalEmails ?? false,
      weeklySummary: user?.settings?.notifications?.weeklySummary ?? true
    },
    language: user?.settings?.language || 'en-US',
    timezone: user?.settings?.timezone || 'GMT-5'
  });
  const [credits, setCredits] = useState({
    imageCredits: user?.credits?.imageCredits || 0,
    videoCredits: user?.credits?.videoCredits || 0
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Update credits when user prop changes
  useEffect(() => {
    if (user) {
      console.log('👤 UserSettingsModal received user:', user);
      console.log('💰 User credits:', user.credits);
      setRole((user.role || 'user').toLowerCase() === 'admin' ? 'admin' : 'user');
      setRunningPlan(normalizeRunningPlanId(user.runningPlan) || 'free');
      setPlanExpiresAt(toDateInputValue(user.planExpiresAt));
      setIsInfluencerAgent(Boolean(user.isInfluencerAgent));
      setInfluencerDiscountPercent(user.influencerDiscountPercent ?? 10);
      setInfluencerPromoCode(user.influencerPromoCode || '');
      setCredits({
        imageCredits: user.credits?.imageCredits || 0,
        videoCredits: user.credits?.videoCredits || 0
      });
      setFormData({
        virtualTryOnEnabled: user.settings?.virtualTryOnEnabled || false,
        fourKEnabled: user.settings?.fourKEnabled || false,
        notifications: {
          contentReady: user.settings?.notifications?.contentReady ?? true,
          promotionalEmails: user.settings?.notifications?.promotionalEmails ?? false,
          weeklySummary: user.settings?.notifications?.weeklySummary ?? true
        },
        language: user.settings?.language || 'en-US',
        timezone: user.settings?.timezone || 'GMT-5'
      });
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const normalizedCode = influencerPromoCode.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
      const expiryIso =
        runningPlan === 'free' ? null : dateInputToExpiryIso(planExpiresAt);
      if (runningPlan !== 'free' && !expiryIso) {
        showNotification('Please set a plan expiry date for Basic or Advance.', 'error');
        setIsSubmitting(false);
        return;
      }
      await onUpdate(user._id, {
        settings: formData,
        credits,
        role,
        runningPlan,
        planExpiresAt: expiryIso,
        isInfluencerAgent,
        influencerDiscountPercent,
        // Only send the code for influencers and when it actually changed.
        ...(isInfluencerAgent && normalizedCode && normalizedCode !== (user?.influencerPromoCode || '')
          ? { influencerPromoCode: normalizedCode }
          : {}),
      });
      onClose();
    } catch (error) {
      showNotification(error.message || 'Failed to update settings', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('notifications.')) {
      const notificationKey = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        notifications: {
          ...prev.notifications,
          [notificationKey]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleCreditChange = (e) => {
    const { name, value } = e.target;
    setCredits(prev => ({
      ...prev,
      [name]: parseInt(value) || 0
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-purple-100">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-purple-700">User Settings - {user?.name}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Business Persona (read-only) */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-purple-700 flex items-center">
              <Briefcase size={20} className="mr-2" />
              Business Persona
            </h3>
            <UserPersonaBadges personas={user?.userPersonas || []} />
          </div>

          <div className="space-y-3 border-t border-purple-100 pt-4">
            <h3 className="text-lg font-semibold text-purple-700 flex items-center">
              <BarChart3 size={20} className="mr-2" />
              Usage metrics
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-lg border border-purple-100 bg-purple-50/60 p-4 text-center">
                <div className="text-2xl font-bold text-purple-700">{user?.fabricsCount ?? 0}</div>
                <div className="text-xs text-purple-500 mt-1">Fabrics added</div>
              </div>
              <div className="rounded-lg border border-purple-100 bg-purple-50/60 p-4 text-center">
                <div className="text-2xl font-bold text-purple-700">{user?.aiGenerationsCount ?? user?.consumption?.creationsCount ?? 0}</div>
                <div className="text-xs text-purple-500 mt-1">AI generations</div>
              </div>
            </div>
          </div>

          {/* Role */}
          <div className="space-y-3 border-t border-purple-100 pt-4">
            <h3 className="text-lg font-semibold text-purple-700 flex items-center">
              <Shield size={20} className="mr-2" />
              Account role
            </h3>
            <p className="text-xs text-gray-500">
              Grant or remove admin access. The last admin cannot be demoted until another admin exists.
            </p>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
              aria-label="User role"
            >
              <option value="user">Normal user</option>
              <option value="admin">Administrator</option>
            </select>
          </div>

          <div className="space-y-3 border-t border-purple-100 pt-4">
            <h3 className="text-lg font-semibold text-purple-700 flex items-center">
              <CreditCard size={20} className="mr-2" />
              Active plan
            </h3>
            <p className="text-xs text-gray-500">
              Set the user&apos;s active plan and expiry. Free Trial has no expiry. Purchases default to 30 days.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl">
              <div>
                <label className="block text-sm font-medium text-purple-700 mb-1">Plan</label>
                <select
                  value={runningPlan}
                  onChange={(e) => {
                    const next = e.target.value;
                    setRunningPlan(next);
                    if (next === 'free') {
                      setPlanExpiresAt('');
                    } else if (!planExpiresAt) {
                      const d = new Date();
                      d.setDate(d.getDate() + 30);
                      setPlanExpiresAt(toDateInputValue(d));
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                  aria-label="Running plan"
                >
                  {RUNNING_PLAN_IDS.map((id) => (
                    <option key={id} value={id}>
                      {RUNNING_PLAN_LABELS[id]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-purple-700 mb-1">Expiry date</label>
                <input
                  type="date"
                  value={planExpiresAt}
                  onChange={(e) => setPlanExpiresAt(e.target.value)}
                  disabled={runningPlan === 'free'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm disabled:bg-gray-100 disabled:text-gray-400"
                  aria-label="Plan expiry date"
                />
                {runningPlan === 'free' ? (
                  <p className="text-xs text-gray-400 mt-1">Not applicable for Free Trial</p>
                ) : null}
              </div>
            </div>
          </div>

          <div className="space-y-3 border-t border-purple-100 pt-4">
            <h3 className="text-lg font-semibold text-purple-700">Influencer agent</h3>
            <p className="text-xs text-gray-500">
              Influencer agents get a unique promo code, shareable link, and dashboard for signups and payments.
            </p>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isInfluencerAgent}
                onChange={(e) => setIsInfluencerAgent(e.target.checked)}
                className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <span className="text-sm text-gray-700">Influencer marketing agent</span>
            </label>
            {isInfluencerAgent && (
              <div className="max-w-xs">
                <label className="block text-sm font-medium text-purple-700 mb-1">
                  Coupon / promo code
                </label>
                <input
                  type="text"
                  value={influencerPromoCode}
                  onChange={(e) =>
                    setInfluencerPromoCode(
                      e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '')
                    )
                  }
                  placeholder="e.g. RIYA9F2C"
                  maxLength={24}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono uppercase focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  At least 4 letters/numbers. Must be unique across all influencers.
                  {user?.influencerPromoCode && (
                    <>
                      {' '}Current:{' '}
                      <span className="font-mono font-semibold text-violet-700">
                        {user.influencerPromoCode}
                      </span>
                    </>
                  )}
                </p>
              </div>
            )}
            {isInfluencerAgent && (
              <div className="max-w-xs">
                <label className="block text-sm font-medium text-purple-700 mb-1">
                  Checkout discount (%)
                </label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={influencerDiscountPercent}
                  onChange={(e) => setInfluencerDiscountPercent(parseInt(e.target.value, 10) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
            )}
          </div>

          {/* User Credits Management */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-purple-700 flex items-center">
              <Zap size={20} className="mr-2" />
              Credit Management
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-purple-700 mb-1">Image Credits</label>
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    name="imageCredits"
                    value={credits.imageCredits}
                    onChange={handleCreditChange}
                    min="0"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="0"
                  />
                  <span className="text-sm text-gray-500">credits</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Used for generating images and virtual try-on content
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-purple-700 mb-1">Video Credits</label>
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    name="videoCredits"
                    value={credits.videoCredits}
                    onChange={handleCreditChange}
                    min="0"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="0"
                  />
                  <span className="text-sm text-gray-500">credits</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Used for generating video content
                </p>
              </div>
            </div>
            
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Image size={16} className="text-purple-600" />
                <span className="text-sm font-medium text-purple-700">Current Credits</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">{credits.imageCredits}</div>
                  <div className="text-xs text-purple-500">Image Credits</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">{credits.videoCredits}</div>
                  <div className="text-xs text-purple-500">Video Credits</div>
                </div>
              </div>
            </div>
          </div>

          {/* Feature Settings */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-purple-700 flex items-center">
              <Zap size={20} className="mr-2" />
              Feature Settings
            </h3>
            <div className="flex items-center">
              <input
                type="checkbox"
                name="virtualTryOnEnabled"
                checked={formData.virtualTryOnEnabled}
                onChange={handleChange}
                className="h-4 w-4 text-purple-500 border-gray-300 rounded focus:ring-purple-500"
              />
              <label className="ml-2 text-sm font-medium text-purple-700 flex items-center">
                Enable Virtual Try-On
                <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                  formData.virtualTryOnEnabled 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {formData.virtualTryOnEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </label>
            </div>
            <p className="text-xs text-gray-500">
              Virtual try-on allows users to see how clothing items look on them using AI technology.
            </p>
            <div className="flex items-center mt-4">
              <input
                type="checkbox"
                name="fourKEnabled"
                checked={formData.fourKEnabled}
                onChange={handleChange}
                className="h-4 w-4 text-purple-500 border-gray-300 rounded focus:ring-purple-500"
              />
              <label className="ml-2 text-sm font-medium text-purple-700 flex items-center">
                Enable 4K Resolution
                <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                  formData.fourKEnabled 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {formData.fourKEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </label>
            </div>
            <p className="text-xs text-gray-500">
              4K output uses 2 image credits per generation instead of 1.
            </p>
          </div>

          {/* Notification Settings */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-purple-700 flex items-center">
              <Mail size={20} className="mr-2" />
              Notification Settings
            </h3>
            <div className="space-y-3">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="notifications.contentReady"
                  checked={formData.notifications.contentReady}
                  onChange={handleChange}
                  className="h-4 w-4 text-purple-500 border-gray-300 rounded focus:ring-purple-500"
                />
                <label className="ml-2 text-sm font-medium text-purple-700">Content Ready Notifications</label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="notifications.promotionalEmails"
                  checked={formData.notifications.promotionalEmails}
                  onChange={handleChange}
                  className="h-4 w-4 text-purple-500 border-gray-300 rounded focus:ring-purple-500"
                />
                <label className="ml-2 text-sm font-medium text-purple-700">Promotional Emails</label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="notifications.weeklySummary"
                  checked={formData.notifications.weeklySummary}
                  onChange={handleChange}
                  className="h-4 w-4 text-purple-500 border-gray-300 rounded focus:ring-purple-500"
                />
                <label className="ml-2 text-sm font-medium text-purple-700">Weekly Summary</label>
              </div>
            </div>
          </div>

          {/* Language and Timezone */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-purple-700 mb-1">Language</label>
              <select
                name="language"
                value={formData.language}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="en-US">English (US)</option>
                <option value="en-GB">English (UK)</option>
                <option value="es-ES">Spanish</option>
                <option value="fr-FR">French</option>
                <option value="de-DE">German</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-purple-700 mb-1">Timezone</label>
              <select
                name="timezone"
                value={formData.timezone}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="GMT-5">Eastern Time (GMT-5)</option>
                <option value="GMT-6">Central Time (GMT-6)</option>
                <option value="GMT-7">Mountain Time (GMT-7)</option>
                <option value="GMT-8">Pacific Time (GMT-8)</option>
                <option value="GMT+0">UTC (GMT+0)</option>
                <option value="GMT+1">Central European Time (GMT+1)</option>
                <option value="GMT+5:30">India Standard Time (GMT+5:30)</option>
              </select>
            </div>
          </div>

          <div className="space-y-3 border-t border-purple-100 pt-4">
            <h3 className="text-lg font-semibold text-purple-700 flex items-center">
              <Download size={20} className="mr-2" />
              Contacts export
            </h3>
            <p className="text-xs text-gray-500">
              Download subscribers and vendor connect leads for this user as a CSV file (AiSensy-compatible format).
            </p>
            <button
              type="button"
              onClick={() => onExportContacts?.(user)}
              disabled={isExportingContacts}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-purple-200 text-purple-700 rounded-lg hover:bg-purple-50 transition-colors text-sm font-medium disabled:opacity-50"
            >
              {isExportingContacts ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Download size={16} />
              )}
              {isExportingContacts ? 'Exporting...' : 'Export contacts CSV'}
            </button>
          </div>

          <div className="flex justify-end space-x-4 pt-6 border-t border-purple-100">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Updating...' : 'Update Settings'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AddUserModal = ({ onClose, onAddUser, showNotification }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'user',
    company: '',
    phone: '',
    isActive: true,
    virtualTryOnEnabled: false
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    // Email is now optional, but if provided, validate format
    if (formData.email.trim() && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    // Phone is now mandatory
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[\+]?[1-9][\d]{0,15}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await onAddUser(formData);
      const virtualTryOnStatus = formData.virtualTryOnEnabled ? 'enabled' : 'disabled';
      showNotification(`User created successfully! Virtual try-on is ${virtualTryOnStatus}.`, 'success');
      onClose();
    } catch (error) {
      showNotification(error.message || 'Failed to create user', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    let newFormData = {
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    };
    
    // Auto-enable virtual try-on for admin users
    if (name === 'role' && value === 'admin') {
      newFormData.virtualTryOnEnabled = true;
    }
    
    setFormData(newFormData);

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-purple-100">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-purple-700">Add New User</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-purple-700 mb-1">Full Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                  errors.name ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter full name"
              />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-purple-700 mb-1">Email (Optional)</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                  errors.email ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter email address (optional)"
              />
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-purple-700 mb-1">Password *</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                  errors.password ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter password"
              />
              {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-purple-700 mb-1">Confirm Password *</label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                  errors.confirmPassword ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Confirm password"
              />
              {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-purple-700 mb-1">Role</label>
              <select
                name="role"
                value={formData.role}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
                <option value="moderator">Moderator</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-purple-700 mb-1">Company</label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Enter company name"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-purple-700 mb-1">Phone *</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                  errors.phone ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter phone number (required)"
              />
              {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
                className="h-4 w-4 text-pink-500 border-gray-300 rounded focus:ring-pink-500"
              />
              <label className="ml-2 text-sm font-medium text-purple-700">Active Account</label>
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                name="virtualTryOnEnabled"
                checked={formData.virtualTryOnEnabled}
                onChange={handleChange}
                className="h-4 w-4 text-purple-500 border-gray-300 rounded focus:ring-purple-500"
              />
              <label className="ml-2 text-sm font-medium text-purple-700 flex items-center">
                <Zap size={16} className="mr-1" />
                Enable Virtual Try-On
                <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                  formData.virtualTryOnEnabled 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {formData.virtualTryOnEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </label>
            </div>
            <div className="col-span-2">
              <p className="text-xs text-gray-500">
                Virtual try-on allows users to see how clothing items look on them using AI technology. 
                By default, this feature is disabled for security and resource management.
              </p>
            </div>
            
            {formData.virtualTryOnEnabled && (
              <div className="col-span-2 bg-purple-50 border border-purple-200 rounded-lg p-3">
                <p className="text-sm text-purple-700 flex items-center">
                  <Zap size={14} className="mr-2" />
                  <strong>Virtual Try-On Enabled:</strong> This user will be able to access the AI-powered virtual try-on feature for fashion products.
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-4 pt-6 border-t border-purple-100">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const USERS_PAGE_SIZE = 25;

const formatAdminDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const AdminPanelView = () => {
  const [activeSubMenu, setActiveSubMenu] = useState('overview');
  const [pendingKits, setPendingKits] = useState([]);
  const [accessRequests, setAccessRequests] = useState([]);
  const [supportRequests, setSupportRequests] = useState([]);
  const [supportStatusFilter, setSupportStatusFilter] = useState('');
  const [supportSearch, setSupportSearch] = useState('');
  const [supportLoading, setSupportLoading] = useState(false);
  const [feedback, setFeedback] = useState([]);
  const [feedbackStats, setFeedbackStats] = useState({ averageRating: 0, totalCount: 0 });
  const [feedbackRatingFilter, setFeedbackRatingFilter] = useState('');
  const [feedbackSearch, setFeedbackSearch] = useState('');
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedKit, setSelectedKit] = useState(null);
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [isEditKitModalOpen, setIsEditKitModalOpen] = useState(false);
  const [kitToEdit, setKitToEdit] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersListSearch, setUsersListSearch] = useState('');
  const [usersRoleFilter, setUsersRoleFilter] = useState('');
  const [usersPersonaFilter, setUsersPersonaFilter] = useState('');
  const [usersVerifiedFilter, setUsersVerifiedFilter] = useState('');
  const [usersInfluencerFilter, setUsersInfluencerFilter] = useState('');
  const [usersPlanFilter, setUsersPlanFilter] = useState('');
  const [usersPage, setUsersPage] = useState(1);
  const [averageConsumptionPerUser, setAverageConsumptionPerUser] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isUserSettingsModalOpen, setIsUserSettingsModalOpen] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [notification, setNotification] = useState(null);
  const [fabricMaterialsToday, setFabricMaterialsToday] = useState(0);
  const [aiCreationsToday, setAiCreationsToday] = useState(0);
  const [dailyActiveUsers, setDailyActiveUsers] = useState(0);
  const [communicationMode, setCommunicationMode] = useState('broadcast'); // 'broadcast' | 'individual'
  const [commTitle, setCommTitle] = useState('');
  const [commMessage, setCommMessage] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [isSendingNotification, setIsSendingNotification] = useState(false);
  const [whatsappConfigured, setWhatsappConfigured] = useState(false);
  const [nanoBananaProvider, setNanoBananaProvider] = useState('replicate');
  const [nanoBananaModels, setNanoBananaModels] = useState(null);
  const [providersConfigured, setProvidersConfigured] = useState({ gemini: false, replicate: false });
  const [isLoadingAppSettings, setIsLoadingAppSettings] = useState(false);
  const [isSavingNanoBananaProvider, setIsSavingNanoBananaProvider] = useState(false);
  const [whatsappTab, setWhatsappTab] = useState('send'); // 'send' | 'templates'
  const [whatsappTemplates, setWhatsappTemplates] = useState([]);
  const [whatsappBroadcasts, setWhatsappBroadcasts] = useState([]);
  const [isLoadingWhatsapp, setIsLoadingWhatsapp] = useState(false);
  const [whatsappMode, setWhatsappMode] = useState('broadcast'); // 'broadcast' | 'individual'
  const [whatsappUseCustom, setWhatsappUseCustom] = useState(false);
  const [selectedWhatsappTemplateId, setSelectedWhatsappTemplateId] = useState('');
  const [whatsappCustomCampaign, setWhatsappCustomCampaign] = useState('');
  const [whatsappParamValues, setWhatsappParamValues] = useState([]);
  const [whatsappMediaUrl, setWhatsappMediaUrl] = useState('');
  const [whatsappMediaFilename, setWhatsappMediaFilename] = useState('');
  const [selectedWhatsappUserIds, setSelectedWhatsappUserIds] = useState([]);
  const [whatsappUserSearch, setWhatsappUserSearch] = useState('');
  const [isSendingWhatsapp, setIsSendingWhatsapp] = useState(false);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    campaignName: '',
    description: '',
    previewText: '',
    paramLabels: [''],
    defaultParams: [''],
    mediaUrl: '',
    mediaFilename: '',
    source: 'Fashdeck Admin',
    isActive: true
  });
  const [vendorConnectRequests, setVendorConnectRequests] = useState([]);
  const [isLoadingVendorConnectRequests, setIsLoadingVendorConnectRequests] = useState(false);
  const [vendorConnectSearch, setVendorConnectSearch] = useState('');
  const [exportingContactsUserId, setExportingContactsUserId] = useState(null);
  const [welcomeEmailUserId, setWelcomeEmailUserId] = useState(null);
  const [isExportingAllContacts, setIsExportingAllContacts] = useState(false);

  const showNotification = (message, type = 'info', duration = 3000) => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification(null);
    }, duration);
  };

  const handleCreateCatalogue = async (kits) => {
    if (!kits || kits.length === 0) {
      showNotification('No kits selected for catalogue creation', 'error');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const title = `Admin Catalogue - ${new Date().toLocaleDateString()}`;
      
      const response = await createPublicCatalogue({
        title,
        description: `Catalogue created by admin with ${kits.length} kit${kits.length > 1 ? 's' : ''}`,
        kitIds: kits.map(kit => kit._id),
        settings: {
          allowDownloads: true,
          showKitNames: true,
          showKitDescriptions: true,
          showBrandInfo: true
        }
      }, token);

      const publicLink = response.data.data;
      showNotification(`Catalogue created successfully! Public URL: ${window.location.origin}/public-catalogue/${publicLink.publicId}`, 'success');
      
      // Copy link to clipboard
      const fullUrl = `${window.location.origin}/public-catalogue/${publicLink.publicId}`;
      navigator.clipboard.writeText(fullUrl).then(() => {
        showNotification('Public link copied to clipboard!', 'info');
      }).catch(() => {
        // Fallback if clipboard API fails
        console.log('Public link:', fullUrl);
      });
      
    } catch (error) {
      console.error('Error creating catalogue:', error);
      showNotification('Failed to create catalogue', 'error');
    }
  };

  const fetchPendingKits = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await getKits(token);
      const allKits = response.data.data.kits;
      // For admin panel, we only want to show pending kits
      const pending = allKits.filter(kit => kit.status === 'pending');
      setPendingKits(pending);
    } catch (err) {
      setError('Failed to fetch pending requests');
      console.error('Error fetching pending kits:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAccessRequests = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await getAccessRequests(token);
      setAccessRequests(response.data.data.accessRequests);
    } catch (err) {
      setError('Failed to fetch access requests');
      console.error('Error fetching access requests:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFabricMaterialsToday = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await getFabricMaterialStatsToday(token);
      const count = response?.data?.data?.fabricMaterialsAddedToday ?? 0;
      setFabricMaterialsToday(count);
    } catch (err) {
      console.error('Error fetching fabric material stats:', err);
    }
  };

  const fetchAICreationsToday = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await getAICreationsStatsToday(token);
      const count = response?.data?.data?.aiCreationsGeneratedToday ?? 0;
      setAiCreationsToday(count);
    } catch (err) {
      console.error('Error fetching AI creation stats:', err);
    }
  };

  const fetchDailyActiveUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await getDailyActiveUsers(token);
      const count = response?.data?.data?.dailyActiveUsersCount ?? 0;
      setDailyActiveUsers(count);
    } catch (err) {
      console.error('Error fetching daily active users:', err);
    }
  };

  useEffect(() => {
    if (activeSubMenu === 'overview') {
      fetchUsers();
      fetchFabricMaterialsToday();
      fetchAICreationsToday();
      fetchDailyActiveUsers();
      fetchAppSettings();
    } else if (activeSubMenu === 'whatsapp') {
      fetchUsers();
      fetchWhatsappData();
    } else if (activeSubMenu === 'users' || activeSubMenu === 'userSettings' || activeSubMenu === 'communication') {
      fetchUsers();
    } else if (activeSubMenu === 'vendorConnectRequests') {
      fetchVendorConnectRequests();
    } else if (activeSubMenu === 'supportRequests') {
      fetchSupportRequests();
    } else if (activeSubMenu === 'feedback') {
      fetchFeedback();
    }
  }, [activeSubMenu]);

  const fetchSupportRequests = async () => {
    setSupportLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await getSupportRequests(token, {
        ...(supportStatusFilter ? { status: supportStatusFilter } : {}),
        ...(supportSearch ? { q: supportSearch } : {}),
        page: 1,
        limit: 100,
      });
      const items = res?.data?.data?.supportRequests || [];
      setSupportRequests(items);
    } catch (err) {
      console.error('Error fetching support requests:', err);
      showNotification(err?.response?.data?.message || 'Failed to fetch support requests', 'error');
    } finally {
      setSupportLoading(false);
    }
  };

  // Refresh support list when filters change (only while on support tab)
  useEffect(() => {
    if (activeSubMenu !== 'supportRequests') return;
    fetchSupportRequests();
  }, [supportStatusFilter, supportSearch, activeSubMenu]);

  const fetchFeedback = async () => {
    setFeedbackLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await getAllFeedback(token, {
        ...(feedbackRatingFilter ? { rating: feedbackRatingFilter } : {}),
        ...(feedbackSearch ? { q: feedbackSearch } : {}),
        page: 1,
        limit: 100,
      });
      setFeedback(res?.data?.data?.feedback || []);
      setFeedbackStats(res?.data?.data?.stats || { averageRating: 0, totalCount: 0 });
    } catch (err) {
      console.error('Error fetching feedback:', err);
      showNotification(err?.response?.data?.message || 'Failed to fetch feedback', 'error');
    } finally {
      setFeedbackLoading(false);
    }
  };

  // Refresh feedback list when filters change (only while on feedback tab)
  useEffect(() => {
    if (activeSubMenu !== 'feedback') return;
    fetchFeedback();
  }, [feedbackRatingFilter, feedbackSearch, activeSubMenu]);

  const fetchVendorConnectRequests = async () => {
    setIsLoadingVendorConnectRequests(true);
    try {
      const res = await getFabricVendorConnectRequests({ page: 1, limit: 100 });
      setVendorConnectRequests(res?.data?.requests || []);
    } catch (err) {
      console.error('Error fetching vendor connect requests:', err);
      showNotification(err?.response?.data?.message || 'Failed to fetch vendor connect requests', 'error');
    } finally {
      setIsLoadingVendorConnectRequests(false);
    }
  };

  const filteredVendorConnectRequests = useMemo(() => {
    const q = String(vendorConnectSearch || '').trim().toLowerCase();
    if (!q) return vendorConnectRequests;
    return (vendorConnectRequests || []).filter((r) => {
      const requesterName = String(r.requesterName || '').toLowerCase();
      const mobileNumber = String(r.mobileNumber || '').toLowerCase();
      const fabricName = String(r.fabricId?.name || '').toLowerCase();
      const vendorName = String(r.vendorId?.name || '').toLowerCase();
      const vendorPhone = String(r.vendorId?.phone || '').toLowerCase();
      const fabricId = String(r.fabricId?._id || r.fabricId || '').toLowerCase();
      const vendorId = String(r.vendorId?._id || r.vendorId || '').toLowerCase();
      return (
        requesterName.includes(q) ||
        mobileNumber.includes(q) ||
        fabricName.includes(q) ||
        vendorName.includes(q) ||
        vendorPhone.includes(q) ||
        fabricId.includes(q) ||
        vendorId.includes(q)
      );
    });
  }, [vendorConnectRequests, vendorConnectSearch]);

  const filteredUsersList = useMemo(() => {
    const q = String(usersListSearch || '').trim().toLowerCase();
    const qDigits = q.replace(/\D/g, '');

    const filtered = (users || []).filter((user) => {
      if (usersRoleFilter && String(user.role || '').toLowerCase() !== usersRoleFilter) {
        return false;
      }

      if (usersPersonaFilter === '__none__') {
        if (normalizePersonasList(user.userPersonas).length > 0) return false;
      } else if (usersPersonaFilter) {
        if (!normalizePersonasList(user.userPersonas).includes(usersPersonaFilter)) return false;
      }

      if (usersVerifiedFilter === 'verified' && !user.isVendorVerified) return false;
      if (usersVerifiedFilter === 'unverified' && user.isVendorVerified) return false;

      if (usersInfluencerFilter === 'yes' && !user.isInfluencerAgent) return false;
      if (usersInfluencerFilter === 'no' && user.isInfluencerAgent) return false;

      const planId = normalizeRunningPlanId(user.runningPlan) || 'free';
      const paid = isPaidPlanActive(user.runningPlan, user.planExpiresAt);
      if (usersPlanFilter === 'paid' && !paid) return false;
      if (usersPlanFilter === 'free' && planId !== 'free') return false;
      if ((usersPlanFilter === 'basic' || usersPlanFilter === 'advanced') && planId !== usersPlanFilter) {
        return false;
      }

      if (!q) return true;

      const name = String(user.name || '').toLowerCase();
      const email = String(user.email || '').toLowerCase();
      const phone = String(user.phone || '').toLowerCase();
      const phoneDigits = phone.replace(/\D/g, '');
      const company = String(user.company || '').toLowerCase();
      const promo = String(user.influencerPromoCode || '').toLowerCase();

      return (
        name.includes(q) ||
        email.includes(q) ||
        phone.includes(q) ||
        company.includes(q) ||
        promo.includes(q) ||
        (qDigits && phoneDigits.includes(qDigits))
      );
    });

    // Latest users on top
    return filtered.sort((a, b) => {
      const aTime = new Date(a.createdAt || 0).getTime();
      const bTime = new Date(b.createdAt || 0).getTime();
      return bTime - aTime;
    });
  }, [
    users,
    usersListSearch,
    usersRoleFilter,
    usersPersonaFilter,
    usersVerifiedFilter,
    usersInfluencerFilter,
    usersPlanFilter,
  ]);

  const usersTotalPages = Math.max(1, Math.ceil(filteredUsersList.length / USERS_PAGE_SIZE));
  const safeUsersPage = Math.min(usersPage, usersTotalPages);

  const paginatedUsersList = useMemo(() => {
    const start = (safeUsersPage - 1) * USERS_PAGE_SIZE;
    return filteredUsersList.slice(start, start + USERS_PAGE_SIZE);
  }, [filteredUsersList, safeUsersPage]);

  const filteredUsersMetrics = useMemo(() => {
    return filteredUsersList.reduce(
      (acc, user) => {
        acc.fabrics += user.fabricsCount || 0;
        acc.aiGenerations += user.aiGenerationsCount || user.consumption?.creationsCount || 0;
        return acc;
      },
      { fabrics: 0, aiGenerations: 0 }
    );
  }, [filteredUsersList]);

  useEffect(() => {
    setUsersPage(1);
  }, [
    usersListSearch,
    usersRoleFilter,
    usersPersonaFilter,
    usersVerifiedFilter,
    usersInfluencerFilter,
    usersPlanFilter,
  ]);

  useEffect(() => {
    if (usersPage > usersTotalPages) {
      setUsersPage(usersTotalPages);
    }
  }, [usersPage, usersTotalPages]);

  const hasUsersListFilters = Boolean(
    usersListSearch.trim() ||
      usersRoleFilter ||
      usersPersonaFilter ||
      usersVerifiedFilter ||
      usersInfluencerFilter ||
      usersPlanFilter
  );

  const clearUsersListFilters = () => {
    setUsersListSearch('');
    setUsersRoleFilter('');
    setUsersPersonaFilter('');
    setUsersVerifiedFilter('');
    setUsersInfluencerFilter('');
    setUsersPlanFilter('');
    setUsersPage(1);
  };

  const paidUsersCount = useMemo(
    () => (users || []).filter((user) => isPaidPlanActive(user.runningPlan, user.planExpiresAt)).length,
    [users]
  );

  const handleApprove = async (formData) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/kits/approve-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          kitId: selectedKit._id,
          name: formData.name,
          brandName: formData.brandName,
          productName: formData.productName,
          model: formData.model,
          contentTypes: formData.contentTypes,
          targetAudience: formData.targetAudience,
          tone: formData.tone,
          additionalNotes: formData.additionalNotes,
          generatedImageUrl: formData.generatedImageUrl,
          videoUrl: formData.videoUrl
        })
      });

      if (!response.ok) {
        let errorMessage = 'Failed to approve kit request';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (parseError) {
          // If JSON parsing fails, try to get text content
          try {
            const errorText = await response.text();
            if (errorText.includes('<!DOCTYPE')) {
              errorMessage = `Server error (${response.status}): ${response.statusText}`;
            } else {
              errorMessage = errorText || errorMessage;
            }
          } catch (textError) {
            errorMessage = `Server error (${response.status}): ${response.statusText}`;
          }
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();

      // Update the kits list with the approved kit
      setPendingKits(prevKits => 
        prevKits.filter(kit => kit._id !== selectedKit._id)
      );

      setSelectedKit(null);
      setIsApprovalModalOpen(false);
      showNotification('Kit request approved successfully!', 'success');
    } catch (error) {
      console.error('Error approving kit:', error);
      setError(error.message);
      showNotification(error.message || 'Failed to approve kit request', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReject = async (kitId) => {
    if (!window.confirm('Are you sure you want to reject this kit request?')) {
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/kits/reject-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          kitId
        })
      });

      if (!response.ok) {
        let errorMessage = 'Failed to reject kit request';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (parseError) {
          // If JSON parsing fails, try to get text content
          try {
            const errorText = await response.text();
            if (errorText.includes('<!DOCTYPE')) {
              errorMessage = `Server error (${response.status}): ${response.statusText}`;
            } else {
              errorMessage = errorText || errorMessage;
            }
          } catch (textError) {
            errorMessage = `Server error (${response.status}): ${response.statusText}`;
          }
        }
        throw new Error(errorMessage);
      }

      // Update the kits list by removing the rejected kit
      setPendingKits(prevKits => 
        prevKits.filter(kit => kit._id !== kitId)
      );

      showNotification('Kit request rejected successfully!', 'success');
    } catch (error) {
      console.error('Error rejecting kit:', error);
      setError(error.message);
      showNotification(error.message || 'Failed to reject kit request', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const createUserWithEmailPhone = async ({ email, phone }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/admin/create-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          name: email || 'New User',
          email,
          phone,
          password: 'test123',
          confirmPassword: 'test123',
          role: 'user',
          isActive: true
        })
      });
      if (!response.ok) {
        let errorMessage = 'Failed to create user';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (parseError) {
          try {
            const errorText = await response.text();
            if (errorText.includes('<!DOCTYPE')) {
              errorMessage = `Server error (${response.status}): ${response.statusText}`;
            } else {
              errorMessage = errorText || errorMessage;
            }
          } catch (textError) {
            errorMessage = `Server error (${response.status}): ${response.statusText}`;
          }
        }
        throw new Error(errorMessage);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  };

  const handleAccessRequestAction = async (requestId, action, notes = '') => {
    setIsLoading(true);
    setError(null);
    try {
      const request = accessRequests.find(r => r._id === requestId);
      if (action === 'approved') {
        // 1. Create user first
        try {
          await createUserWithEmailPhone({ email: request.email, phone: request.phone });
        } catch (userErr) {
          showNotification(userErr.message || 'Failed to create user', 'error');
          setIsLoading(false);
          return;
        }
      }
      // 2. Update access request status
      const token = localStorage.getItem('token');
      await updateAccessRequestStatus(requestId, { status: action, notes }, token);
      setAccessRequests(prevRequests =>
        prevRequests.map(request =>
          request._id === requestId
            ? { ...request, status: action, notes, reviewedAt: new Date() }
            : request
        )
      );
      showNotification(`Access request ${action} successfully!`, 'success');
    } catch (error) {
      console.error(`Error ${action}ing access request:`, error);
      setError(error.message);
      showNotification(error.message || `Failed to ${action} access request`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAccessRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this access request?')) {
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      await deleteAccessRequest(requestId, token);
      
      // Remove from local state
      setAccessRequests(prevRequests => 
        prevRequests.filter(request => request._id !== requestId)
      );
      
      showNotification('Access request deleted successfully!', 'success');
    } catch (error) {
      console.error('Error deleting access request:', error);
      setError(error.message);
      showNotification(error.message || 'Failed to delete access request', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddUser = async (userData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/admin/create-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(userData)
      });

      if (!response.ok) {
        let errorMessage = 'Failed to create user';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (parseError) {
          // If JSON parsing fails, try to get text content
          try {
            const errorText = await response.text();
            if (errorText.includes('<!DOCTYPE')) {
              errorMessage = `Server error (${response.status}): ${response.statusText}`;
            } else {
              errorMessage = errorText || errorMessage;
            }
          } catch (textError) {
            errorMessage = `Server error (${response.status}): ${response.statusText}`;
          }
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  };

  const handleUpdateKit = async (kitId, updateData) => {
    try {
      await updateKit(kitId, updateData);
      // Refresh the pending kits list
      await fetchPendingKits();
    } catch (error) {
      throw error;
    }
  };

  const fetchUsers = async () => {
    setIsLoadingUsers(true);
    try {
      const response = await fetch(`${API_BASE_URL}/users`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }
      
      const data = await response.json();
      const rawUsers = data?.data?.users || data?.users || [];
      const normalizedUsers = rawUsers.map((user) => {
        const aiGenerationsCount =
          user?.aiGenerationsCount ?? user?.consumption?.creationsCount ?? 0;
        return {
          ...user,
          userPersonas: normalizePersonasList(user?.userPersonas),
          fabricsCount: user?.fabricsCount || 0,
          aiGenerationsCount,
          consumption: {
            imageCredits: user?.consumption?.imageCredits || 0,
            videoCredits: user?.consumption?.videoCredits || 0,
            total: user?.consumption?.total || 0,
            creationsCount: aiGenerationsCount,
          },
          lastUsedAt: user?.lastUsedAt || null,
        };
      });
      console.log('👥 Fetched users data:', normalizedUsers);
      setUsers(normalizedUsers);

      const apiAverage = data?.data?.stats?.averageConsumptionPerUser;
      if (typeof apiAverage === 'number') {
        setAverageConsumptionPerUser(apiAverage);
      } else {
        const totalConsumption = normalizedUsers.reduce(
          (sum, user) => sum + (user.consumption?.total || 0),
          0
        );
        setAverageConsumptionPerUser(
          normalizedUsers.length > 0
            ? Math.round((totalConsumption / normalizedUsers.length) * 10) / 10
            : 0
        );
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      showNotification('Failed to fetch users', 'error');
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const handleExportUserContacts = async (user) => {
    if (!user?._id) return;
    setExportingContactsUserId(user._id);
    try {
      const safeName = String(user.name || 'user').replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 40);
      await downloadContactsCsv(`${API_BASE_URL}/users/${user._id}/contacts/export`, `Contacts-${safeName}.csv`);
      showNotification('Contacts exported successfully', 'success');
    } catch (error) {
      console.error('Error exporting user contacts:', error);
      showNotification(error.message || 'Failed to export contacts', 'error');
    } finally {
      setExportingContactsUserId(null);
    }
  };

  const handleExportAllContacts = async () => {
    setIsExportingAllContacts(true);
    try {
      await downloadContactsCsv(`${API_BASE_URL}/users/admin/contacts/export`, 'Contacts-All.csv');
      showNotification('All contacts exported successfully', 'success');
    } catch (error) {
      console.error('Error exporting all contacts:', error);
      showNotification(error.message || 'Failed to export all contacts', 'error');
    } finally {
      setIsExportingAllContacts(false);
    }
  };

  const downloadContactsCsv = async (url, filename) => {
    const token = localStorage.getItem('token');
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      let message = 'Failed to export contacts';
      try {
        const err = await response.json();
        message = err.message || message;
      } catch {
        // ignore non-JSON error bodies
      }
      throw new Error(message);
    }

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objectUrl);
  };

  const handleResendWelcomeEmail = async (user) => {
    if (!user?.email) {
      showNotification('This user has no email address', 'error');
      return;
    }
    setWelcomeEmailUserId(user._id);
    try {
      const token = localStorage.getItem('token');
      const response = await resendWelcomeEmail(user._id, token);
      showNotification(response.data?.message || `Welcome email sent to ${user.email}`, 'success');
    } catch (error) {
      console.error('Error resending welcome email:', error);
      showNotification(error.response?.data?.message || 'Failed to send welcome email', 'error');
    } finally {
      setWelcomeEmailUserId(null);
    }
  };

  const toggleVendorVerified = async (user) => {
    try {
      const token = localStorage.getItem('token');
      const nextVerified = !Boolean(user.isVendorVerified);
      await updateVendorVerified(user._id, nextVerified, token);
      showNotification(nextVerified ? 'Vendor marked as verified' : 'Vendor marked as unverified', 'success');
      fetchUsers();
    } catch (error) {
      console.error('Error updating vendor verification:', error);
      showNotification(error.response?.data?.message || 'Failed to update vendor verification', 'error');
    }
  };

  const updateUserSettings = async (userId, payload) => {
    console.log('🔧 Frontend sending user settings update:', { userId, payload });

    const response = await fetch(`${API_BASE_URL}/users/${userId}/settings`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({
        settings: payload.settings,
        credits: payload.credits,
        ...(payload.role !== undefined ? { role: payload.role } : {}),
        ...(payload.runningPlan !== undefined ? { runningPlan: payload.runningPlan } : {}),
        ...(payload.planExpiresAt !== undefined ? { planExpiresAt: payload.planExpiresAt } : {}),
        ...(payload.isInfluencerAgent !== undefined
          ? { isInfluencerAgent: payload.isInfluencerAgent }
          : {}),
        ...(payload.influencerDiscountPercent !== undefined
          ? { influencerDiscountPercent: payload.influencerDiscountPercent }
          : {}),
        ...(payload.influencerPromoCode !== undefined
          ? { influencerPromoCode: payload.influencerPromoCode }
          : {})
      })
    });

    if (!response.ok) {
      let message = 'Failed to update user settings';
      try {
        const err = await response.json();
        if (err?.message) message = err.message;
      } catch (_) {
        const text = await response.text().catch(() => '');
        if (text) message = text.slice(0, 200);
      }
      console.error('❌ Backend response error:', response.status, message);
      throw new Error(message);
    }

    const responseData = await response.json();
    console.log('✅ Backend response:', responseData);

    const updatedRole = responseData?.data?.user?.role;
    const updatedInfluencer = responseData?.data?.user?.isInfluencerAgent;
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const part = token.split('.')[1];
        const decoded = JSON.parse(atob(part));
        const selfId = decoded.userId;
        if (selfId && String(selfId) === String(userId)) {
          if (updatedRole) localStorage.setItem('role', updatedRole);
          if (updatedInfluencer !== undefined) {
            localStorage.setItem('isInfluencerAgent', updatedInfluencer ? 'true' : 'false');
          }
        }
      }
    } catch (_) {
      /* ignore */
    }

    showNotification('User settings updated successfully', 'success');
    fetchUsers();
  };

  const openUserSettingsModal = (user) => {
    console.log('🔧 Opening user settings modal for user:', user);
    console.log('💰 User credits in openUserSettingsModal:', user.credits);
    setSelectedUser(user);
    setIsUserSettingsModalOpen(true);
  };

  const openEditKitModal = (kit) => {
    setKitToEdit(kit);
    setIsEditKitModalOpen(true);
  };

  const handleSendBroadcast = async () => {
    if (!commTitle.trim() || !commMessage.trim()) {
      showNotification('Please enter both title and message', 'error');
      return;
    }
    setIsSendingNotification(true);
    try {
      const token = localStorage.getItem('token');
      const res = await broadcastNotification(commTitle.trim(), commMessage.trim(), token);
      const d = res.data?.data || {};
      showNotification(
        `Sent! Push: ${d.pushSent || 0}, In-app: ${d.inAppCreated || 0} users`,
        'success'
      );
      setCommTitle('');
      setCommMessage('');
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to broadcast', 'error');
    } finally {
      setIsSendingNotification(false);
    }
  };

  const handleSendToUsers = async () => {
    if (!commTitle.trim() || !commMessage.trim()) {
      showNotification('Please enter both title and message', 'error');
      return;
    }
    if (!selectedUserIds.length) {
      showNotification('Please select at least one user', 'error');
      return;
    }
    setIsSendingNotification(true);
    try {
      const token = localStorage.getItem('token');
      const res = await sendNotificationToUsers(commTitle.trim(), commMessage.trim(), selectedUserIds, token);
      const d = res.data?.data || {};
      showNotification(
        `Sent! Push: ${d.pushSent || 0}, In-app: ${d.inAppCreated || 0} users`,
        'success'
      );
      setCommTitle('');
      setCommMessage('');
      setSelectedUserIds([]);
      setUserSearchQuery('');
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to send', 'error');
    } finally {
      setIsSendingNotification(false);
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUserIds(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const fetchWhatsappData = async () => {
    setIsLoadingWhatsapp(true);
    try {
      const token = localStorage.getItem('token');
      const [statusRes, templatesRes, broadcastsRes] = await Promise.all([
        getWhatsAppStatus(token),
        getWhatsAppTemplates(token),
        getWhatsAppBroadcasts(token)
      ]);
      setWhatsappConfigured(statusRes.data?.data?.configured || false);
      setWhatsappTemplates(templatesRes.data?.data?.templates || []);
      setWhatsappBroadcasts(broadcastsRes.data?.data?.broadcasts || []);
    } catch (err) {
      console.error('Error fetching WhatsApp data:', err);
      showNotification(err.response?.data?.message || 'Failed to load WhatsApp data', 'error');
    } finally {
      setIsLoadingWhatsapp(false);
    }
  };

  const fetchAppSettings = async () => {
    setIsLoadingAppSettings(true);
    try {
      const token = localStorage.getItem('token');
      const response = await getAppSettings(token);
      const data = response.data?.data || {};
      setNanoBananaProvider(data.nanoBananaProvider || 'replicate');
      setNanoBananaModels(data.models || null);
      setProvidersConfigured(data.providersConfigured || { gemini: false, replicate: false });
    } catch (err) {
      console.error('Error fetching app settings:', err);
      showNotification(err.response?.data?.message || 'Failed to load AI provider settings', 'error');
    } finally {
      setIsLoadingAppSettings(false);
    }
  };

  const handleNanoBananaProviderChange = async (provider) => {
    if (provider === nanoBananaProvider || isSavingNanoBananaProvider) return;

    setIsSavingNanoBananaProvider(true);
    try {
      const token = localStorage.getItem('token');
      const response = await updateNanoBananaProvider(provider, token);
      const updatedProvider = response.data?.data?.nanoBananaProvider || provider;
      setNanoBananaProvider(updatedProvider);
      showNotification(
        updatedProvider === 'gemini'
          ? 'Generate API now uses Vertex AI'
          : 'Generate API now uses Replicate',
        'success'
      );
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to update AI provider', 'error');
    } finally {
      setIsSavingNanoBananaProvider(false);
    }
  };

  const selectedWhatsappTemplate = useMemo(
    () => whatsappTemplates.find((t) => t._id === selectedWhatsappTemplateId) || null,
    [whatsappTemplates, selectedWhatsappTemplateId]
  );

  useEffect(() => {
    if (!selectedWhatsappTemplate) {
      setWhatsappParamValues([]);
      return;
    }
    const defaults = selectedWhatsappTemplate.defaultParams?.length
      ? selectedWhatsappTemplate.defaultParams
      : (selectedWhatsappTemplate.paramLabels || []).map(() => '');
    setWhatsappParamValues(defaults.map(String));
  }, [selectedWhatsappTemplate]);

  const buildWhatsappPayload = () => {
    const payload = {
      templateParams: whatsappParamValues.map(String)
    };

    if (whatsappUseCustom) {
      payload.campaignName = whatsappCustomCampaign.trim();
    } else {
      payload.templateId = selectedWhatsappTemplateId;
    }

    if (whatsappMediaUrl.trim()) {
      payload.media = {
        url: whatsappMediaUrl.trim(),
        filename: whatsappMediaFilename.trim() || 'media'
      };
    }

    return payload;
  };

  const handleWhatsappBroadcast = async () => {
    if (!whatsappUseCustom && !selectedWhatsappTemplateId) {
      showNotification('Please select a template or use custom campaign', 'error');
      return;
    }
    if (whatsappUseCustom && !whatsappCustomCampaign.trim()) {
      showNotification('Please enter the AiSensy campaign name', 'error');
      return;
    }

    setIsSendingWhatsapp(true);
    try {
      const token = localStorage.getItem('token');
      const res = await broadcastWhatsApp(buildWhatsappPayload(), token);
      const d = res.data?.data || {};
      showNotification(
        `WhatsApp sent! ${d.successCount || 0} delivered, ${d.failureCount || 0} failed, ${d.skippedNoPhone || 0} skipped`,
        'success'
      );
      fetchWhatsappData();
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to send WhatsApp broadcast', 'error');
    } finally {
      setIsSendingWhatsapp(false);
    }
  };

  const handleWhatsappSendToUsers = async () => {
    if (!whatsappUseCustom && !selectedWhatsappTemplateId) {
      showNotification('Please select a template or use custom campaign', 'error');
      return;
    }
    if (whatsappUseCustom && !whatsappCustomCampaign.trim()) {
      showNotification('Please enter the AiSensy campaign name', 'error');
      return;
    }
    if (!selectedWhatsappUserIds.length) {
      showNotification('Please select at least one user', 'error');
      return;
    }

    setIsSendingWhatsapp(true);
    try {
      const token = localStorage.getItem('token');
      const res = await sendWhatsAppToUsers(
        { ...buildWhatsappPayload(), userIds: selectedWhatsappUserIds },
        token
      );
      const d = res.data?.data || {};
      showNotification(
        `WhatsApp sent! ${d.successCount || 0} delivered, ${d.failureCount || 0} failed`,
        'success'
      );
      setSelectedWhatsappUserIds([]);
      setWhatsappUserSearch('');
      fetchWhatsappData();
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to send WhatsApp messages', 'error');
    } finally {
      setIsSendingWhatsapp(false);
    }
  };

  const toggleWhatsappUserSelection = (userId) => {
    setSelectedWhatsappUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const openTemplateModal = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setTemplateForm({
        name: template.name || '',
        campaignName: template.campaignName || '',
        description: template.description || '',
        previewText: template.previewText || '',
        paramLabels: template.paramLabels?.length ? template.paramLabels : [''],
        defaultParams: template.defaultParams?.length ? template.defaultParams : [''],
        mediaUrl: template.mediaUrl || '',
        mediaFilename: template.mediaFilename || '',
        source: template.source || 'Fashdeck Admin',
        isActive: template.isActive !== false
      });
    } else {
      setEditingTemplate(null);
      setTemplateForm({
        name: '',
        campaignName: '',
        description: '',
        previewText: '',
        paramLabels: [''],
        defaultParams: [''],
        mediaUrl: '',
        mediaFilename: '',
        source: 'Fashdeck Admin',
        isActive: true
      });
    }
    setIsTemplateModalOpen(true);
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name.trim() || !templateForm.campaignName.trim()) {
      showNotification('Template name and campaign name are required', 'error');
      return;
    }

    const labels = templateForm.paramLabels.filter((l) => l.trim());
    const defaults = templateForm.defaultParams.slice(0, labels.length || templateForm.defaultParams.length);

    const data = {
      ...templateForm,
      paramLabels: labels.length ? labels : templateForm.defaultParams.filter((p) => p.trim()),
      defaultParams: defaults.filter((p) => p.trim())
    };

    try {
      const token = localStorage.getItem('token');
      if (editingTemplate) {
        await updateWhatsAppTemplate(editingTemplate._id, data, token);
        showNotification('Template updated', 'success');
      } else {
        await createWhatsAppTemplate(data, token);
        showNotification('Template created', 'success');
      }
      setIsTemplateModalOpen(false);
      fetchWhatsappData();
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to save template', 'error');
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Delete this WhatsApp template?')) return;
    try {
      const token = localStorage.getItem('token');
      await deleteWhatsAppTemplate(id, token);
      showNotification('Template deleted', 'success');
      if (selectedWhatsappTemplateId === id) setSelectedWhatsappTemplateId('');
      fetchWhatsappData();
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to delete template', 'error');
    }
  };

  const subMenus = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'userSettings', label: 'User Settings', icon: Settings },
    { id: 'communication', label: 'Communication', icon: MessageSquare },
    { id: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
    { id: 'vendorConnectRequests', label: 'Vendor Connect Requests', icon: MessageCircle },
    { id: 'supportRequests', label: 'Support Requests', icon: Headphones },
    { id: 'feedback', label: 'Feedback', icon: Star }
  ];

  const renderContent = () => {
    switch (activeSubMenu) {
      case 'overview':
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-purple-700">Overview</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 sm:gap-6">
              <div className="bg-white rounded-xl shadow-md border border-purple-100 p-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-purple-700">Total Users</h3>
                  <Users size={24} className="text-pink-400" />
                </div>
                <p className="text-3xl font-bold text-purple-900">{users.length}</p>
                <p className="text-sm text-purple-500 mt-1">Active Accounts</p>
              </div>
              <div className="bg-white rounded-xl shadow-md border border-purple-100 p-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-purple-700">Avg Consumption / User</h3>
                  <Zap size={24} className="text-pink-400" />
                </div>
                <p className="text-3xl font-bold text-purple-900">{averageConsumptionPerUser}</p>
                <p className="text-sm text-purple-500 mt-1">Credits used (image + video)</p>
              </div>
              <div className="bg-white rounded-xl shadow-md border border-purple-100 p-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-purple-700">Daily Active Users</h3>
                  <Activity size={24} className="text-pink-400" />
                </div>
                <p className="text-3xl font-bold text-purple-900">{dailyActiveUsers}</p>
                <p className="text-sm text-purple-500 mt-1">Created AI or uploaded fabric today</p>
              </div>
              <div className="bg-white rounded-xl shadow-md border border-purple-100 p-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-purple-700">Fabric Materials Added Today</h3>
                  <Layers size={24} className="text-pink-400" />
                </div>
                <p className="text-3xl font-bold text-purple-900">{fabricMaterialsToday}</p>
                <p className="text-sm text-purple-500 mt-1">New materials added today</p>
              </div>
              <div className="bg-white rounded-xl shadow-md border border-purple-100 p-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-purple-700">AI Creations Generated Today</h3>
                  <Sparkles size={24} className="text-pink-400" />
                </div>
                <p className="text-3xl font-bold text-purple-900">{aiCreationsToday}</p>
                <p className="text-sm text-purple-500 mt-1">Generated today</p>
              </div>
            </div>
            
            {/* Credit Management Overview */}
            <div className="bg-white rounded-xl shadow-md border border-purple-100 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-purple-700 flex items-center">
                  <Zap size={20} className="mr-2" />
                  Credit Management Overview
                </h3>
                <button
                  onClick={() => setActiveSubMenu('users')}
                  className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors text-sm"
                >
                  Manage Credits
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-purple-700">Total Image Credits</h4>
                    <Image size={16} className="text-purple-600" />
                  </div>
                  <p className="text-2xl font-bold text-purple-900">
                    {users.reduce((total, user) => total + (user.credits?.imageCredits || 0), 0)}
                  </p>
                  <p className="text-xs text-purple-500 mt-1">Distributed across all users</p>
                </div>
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-purple-700">Total Video Credits</h4>
                    <Video size={16} className="text-purple-600" />
                  </div>
                  <p className="text-2xl font-bold text-purple-900">
                    {users.reduce((total, user) => total + (user.credits?.videoCredits || 0), 0)}
                  </p>
                  <p className="text-xs text-purple-500 mt-1">Distributed across all users</p>
                </div>
              </div>
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-700">
                  <strong>Quick Actions:</strong> Click "Manage Credits" to view and update individual user credits, 
                  or navigate to the Users section to access detailed user management.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md border border-purple-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-purple-700 flex items-center">
                  <Sparkles size={20} className="mr-2" />
                  Generate API — Nano Banana Provider
                </h3>
                {isLoadingAppSettings && (
                  <Loader2 size={18} className="animate-spin text-purple-400" />
                )}
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Switch the /realtime/generate image pipeline between Replicate and Google Vertex AI (same as Magic Edit v2).
                The same underlying Nano Banana models are used on both sides.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <button
                  type="button"
                  onClick={() => handleNanoBananaProviderChange('replicate')}
                  disabled={isSavingNanoBananaProvider || isLoadingAppSettings}
                  className={`rounded-xl border p-4 text-left transition-all ${
                    nanoBananaProvider === 'replicate'
                      ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200'
                      : 'border-gray-200 hover:border-purple-200 hover:bg-purple-50/40'
                  } disabled:opacity-60`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-purple-900">Replicate</span>
                    {nanoBananaProvider === 'replicate' && (
                      <BadgeCheck size={18} className="text-purple-600" />
                    )}
                  </div>
                  <p className="text-xs text-gray-600">
                    Models: {nanoBananaModels?.replicate?.standard || 'google/nano-banana'}
                    {' / '}
                    {nanoBananaModels?.replicate?.pro || 'google/nano-banana-pro'}
                  </p>
                  <p className={`text-xs mt-2 ${providersConfigured.replicate ? 'text-green-700' : 'text-amber-700'}`}>
                    {providersConfigured.replicate ? 'API token configured' : 'REPLICATE_API_TOKEN not configured'}
                  </p>
                </button>

                <button
                  type="button"
                  onClick={() => handleNanoBananaProviderChange('gemini')}
                  disabled={isSavingNanoBananaProvider || isLoadingAppSettings}
                  className={`rounded-xl border p-4 text-left transition-all ${
                    nanoBananaProvider === 'gemini'
                      ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200'
                      : 'border-gray-200 hover:border-purple-200 hover:bg-purple-50/40'
                  } disabled:opacity-60`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-purple-900">Vertex AI</span>
                    {nanoBananaProvider === 'gemini' && (
                      <BadgeCheck size={18} className="text-purple-600" />
                    )}
                  </div>
                  <p className="text-xs text-gray-600">
                    Models: {nanoBananaModels?.gemini?.standard || 'gemini-nano-banana-2.1'}
                    {' / '}
                    {nanoBananaModels?.gemini?.pro || 'gemini-nano-banana-2.1'}
                  </p>
                  <p className={`text-xs mt-2 ${providersConfigured.gemini ? 'text-green-700' : 'text-amber-700'}`}>
                    {providersConfigured.gemini ? 'Vertex AI configured (GOOGLE_CLOUD_PROJECT)' : 'GOOGLE_CLOUD_PROJECT not configured'}
                  </p>
                </button>
              </div>

              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  Active provider: <strong>{nanoBananaProvider === 'gemini' ? 'Vertex AI' : 'Replicate'}</strong>.
                  {nanoBananaProvider === 'gemini' && ' Uses gemini-nano-banana-2.1 (Nano Banana 2.1) on global Vertex; fallback: gemini-3.1-flash-image. 4K uses gemini-3-pro-image.'}
                </p>
              </div>
            </div>
          </div>
        );
      case 'users':
        return (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <div>
                <h2 className="text-2xl font-bold text-purple-700">Users</h2>
                <p className="text-sm text-gray-600 mt-1">
                  {isLoadingUsers
                    ? 'Loading users…'
                    : `Showing ${paginatedUsersList.length ? ((safeUsersPage - 1) * USERS_PAGE_SIZE) + 1 : 0}–${Math.min(safeUsersPage * USERS_PAGE_SIZE, filteredUsersList.length)} of ${filteredUsersList.length} users`}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={handleExportAllContacts}
                  disabled={isExportingAllContacts}
                  className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors flex items-center gap-2 font-medium disabled:opacity-50"
                >
                  {isExportingAllContacts ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Download size={16} />
                  )}
                  {isExportingAllContacts ? 'Exporting...' : 'Export All Contacts'}
                </button>
                <button
                  onClick={() => setIsAddUserModalOpen(true)}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2 font-medium"
                >
                  <UserPlus size={16} />
                  Add User
                </button>
                <button
                  onClick={fetchUsers}
                  className="px-4 py-2 bg-white border border-purple-200 text-purple-700 rounded-lg hover:bg-purple-50 transition-colors flex items-center gap-2 font-medium disabled:opacity-50"
                  disabled={isLoadingUsers}
                >
                  <RefreshCw size={16} className={isLoadingUsers ? 'animate-spin' : ''} />
                  {isLoadingUsers ? 'Loading...' : 'Refresh'}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl shadow-md border border-purple-100 p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-purple-700">Fabrics added</p>
                  <p className="text-xs text-gray-500 mt-0.5">Across shown users</p>
                </div>
                <div className="flex items-center gap-2">
                  <Layers size={18} className="text-pink-400" />
                  <span className="text-2xl font-bold text-purple-900">{filteredUsersMetrics.fabrics}</span>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-md border border-purple-100 p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-purple-700">AI generations</p>
                  <p className="text-xs text-gray-500 mt-0.5">Across shown users</p>
                </div>
                <div className="flex items-center gap-2">
                  <Sparkles size={18} className="text-pink-400" />
                  <span className="text-2xl font-bold text-purple-900">{filteredUsersMetrics.aiGenerations}</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setUsersPlanFilter((current) => (current === 'paid' ? '' : 'paid'))}
                className={`rounded-xl shadow-md border p-4 flex items-center justify-between text-left transition-colors ${
                  usersPlanFilter === 'paid'
                    ? 'bg-emerald-50 border-emerald-300'
                    : 'bg-white border-purple-100 hover:border-emerald-200'
                }`}
              >
                <div>
                  <p className="text-sm font-medium text-purple-700">Paid users</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {usersPlanFilter === 'paid' ? 'Showing paid only' : 'Basic and Advance, still active'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <CreditCard size={18} className="text-emerald-500" />
                  <span className="text-2xl font-bold text-purple-900">{paidUsersCount}</span>
                </div>
              </button>
            </div>

            <div className="bg-white rounded-xl shadow-md border border-purple-100 p-4">
              <div className="flex flex-col xl:flex-row gap-3 xl:items-end">
                <div className="flex-1 min-w-0">
                  <label className="text-xs font-semibold text-gray-600">Search</label>
                  <div className="relative mt-1">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    <input
                      value={usersListSearch}
                      onChange={(e) => setUsersListSearch(e.target.value)}
                      placeholder="Name, email, phone, company, promo code…"
                      className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-200"
                    />
                  </div>
                </div>
                <div className="w-full sm:w-40">
                  <label className="text-xs font-semibold text-gray-600">Role</label>
                  <select
                    value={usersRoleFilter}
                    onChange={(e) => setUsersRoleFilter(e.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-200"
                  >
                    <option value="">All</option>
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="w-full sm:w-52">
                  <label className="text-xs font-semibold text-gray-600">Persona</label>
                  <select
                    value={usersPersonaFilter}
                    onChange={(e) => setUsersPersonaFilter(e.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-200"
                  >
                    <option value="">All</option>
                    <option value="__none__">Not set</option>
                    {USER_PERSONAS.map((persona) => (
                      <option key={persona} value={persona}>
                        {USER_PERSONA_LABELS[persona] || persona}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="w-full sm:w-40">
                  <label className="text-xs font-semibold text-gray-600">Verified</label>
                  <select
                    value={usersVerifiedFilter}
                    onChange={(e) => setUsersVerifiedFilter(e.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-200"
                  >
                    <option value="">All</option>
                    <option value="verified">Verified</option>
                    <option value="unverified">Unverified</option>
                  </select>
                </div>
                <div className="w-full sm:w-44">
                  <label className="text-xs font-semibold text-gray-600">Plan</label>
                  <select
                    value={usersPlanFilter}
                    onChange={(e) => setUsersPlanFilter(e.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-200"
                  >
                    <option value="">All</option>
                    <option value="paid">Paid</option>
                    <option value="free">Free Trial</option>
                    <option value="basic">Basic</option>
                    <option value="advanced">Advance</option>
                  </select>
                </div>
                <div className="w-full sm:w-40">
                  <label className="text-xs font-semibold text-gray-600">Influencer</label>
                  <select
                    value={usersInfluencerFilter}
                    onChange={(e) => setUsersInfluencerFilter(e.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-200"
                  >
                    <option value="">All</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>
                {hasUsersListFilters && (
                  <button
                    type="button"
                    onClick={clearUsersListFilters}
                    className="px-3 py-2 text-sm font-medium text-purple-700 border border-purple-200 rounded-lg hover:bg-purple-50 transition-colors whitespace-nowrap"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            </div>

            {isLoadingUsers ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
              </div>
            ) : users.length === 0 ? (
              <div className="bg-white rounded-xl shadow-md border border-purple-100 p-8 text-center">
                <Users size={48} className="mx-auto text-pink-200 mb-4" />
                <p className="text-gray-600">No users found.</p>
                <button
                  onClick={fetchUsers}
                  className="mt-4 px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors"
                >
                  Load Users
                </button>
              </div>
            ) : filteredUsersList.length === 0 ? (
              <div className="bg-white rounded-xl shadow-md border border-purple-100 p-8 text-center">
                <Search size={48} className="mx-auto text-pink-200 mb-4" />
                <p className="text-gray-600">No users match these filters.</p>
                <button
                  type="button"
                  onClick={clearUsersListFilters}
                  className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Clear filters
                </button>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-md border border-purple-100 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-purple-100">
                    <thead className="bg-gradient-to-r from-pink-50 to-purple-50 sticky top-0 z-10">
                      <tr>
                        <th className="px-4 py-3.5 text-left text-xs font-semibold text-purple-700 uppercase tracking-wider">Name</th>
                        <th className="px-4 py-3.5 text-left text-xs font-semibold text-purple-700 uppercase tracking-wider">Email</th>
                        <th className="px-4 py-3.5 text-left text-xs font-semibold text-purple-700 uppercase tracking-wider">Phone</th>
                        <th className="px-4 py-3.5 text-left text-xs font-semibold text-purple-700 uppercase tracking-wider">Role</th>
                        <th className="px-4 py-3.5 text-left text-xs font-semibold text-purple-700 uppercase tracking-wider">Plan</th>
                        <th className="px-4 py-3.5 text-left text-xs font-semibold text-purple-700 uppercase tracking-wider min-w-[180px]">Persona</th>
                        <th className="px-4 py-3.5 text-left text-xs font-semibold text-purple-700 uppercase tracking-wider">Verified</th>
                        <th className="px-4 py-3.5 text-left text-xs font-semibold text-purple-700 uppercase tracking-wider">Credits</th>
                        <th className="px-4 py-3.5 text-left text-xs font-semibold text-purple-700 uppercase tracking-wider">Consumption</th>
                        <th className="px-4 py-3.5 text-left text-xs font-semibold text-purple-700 uppercase tracking-wider">Fabrics</th>
                        <th className="px-4 py-3.5 text-left text-xs font-semibold text-purple-700 uppercase tracking-wider">AI Generations</th>
                        <th className="px-4 py-3.5 text-left text-xs font-semibold text-purple-700 uppercase tracking-wider">Last Used</th>
                        <th className="px-4 py-3.5 text-left text-xs font-semibold text-purple-700 uppercase tracking-wider">Virtual Try-On</th>
                        <th className="px-4 py-3.5 text-left text-xs font-semibold text-purple-700 uppercase tracking-wider w-36">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-purple-50">
                      {paginatedUsersList.map(user => (
                        <tr key={user._id} className="hover:bg-purple-50/50 transition-colors">
                          <td className="px-4 py-3">
                            <div className="text-sm font-medium text-purple-900 truncate max-w-[180px]" title={user.name}>{user.name}</div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm text-gray-700 truncate max-w-[200px]" title={user.email || 'N/A'}>{user.email || '—'}</div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm text-gray-700">{user.phone || '—'}</div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full ${
                              user.role === 'admin' 
                                ? 'bg-purple-100 text-purple-800' 
                                : 'bg-gray-100 text-gray-700'
                            }`}>
                              {user.role}
                            </span>
                            {user.isInfluencerAgent && (
                              <span className="ml-1 inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-fuchsia-100 text-fuchsia-800">
                                Influencer
                              </span>
                            )}
                            {user.influencerPromoCode && (
                              <span className="block text-xs font-mono text-violet-600 mt-0.5">{user.influencerPromoCode}</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {(() => {
                              const planId = normalizeRunningPlanId(user.runningPlan) || 'free';
                              const paid = isPaidPlanActive(user.runningPlan, user.planExpiresAt);
                              const expiry = paid ? formatPlanExpiryDate(user.planExpiresAt) : null;
                              return (
                                <div>
                                  <span className={`inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full ${
                                    paid ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'
                                  }`}>
                                    {runningPlanLabel(planId)}
                                  </span>
                                  {expiry && (
                                    <div className="text-xs text-gray-500 mt-0.5">until {expiry}</div>
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                          <td className="px-4 py-3 align-top">
                            <UserPersonaBadges personas={user.userPersonas || []} />
                          </td>
                          <td className="px-4 py-3">
                            {user.role === 'admin' ? (
                              <span className="text-xs text-gray-400">—</span>
                            ) : (
                              <button
                                type="button"
                                onClick={() => toggleVendorVerified(user)}
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
                                  user.isVendorVerified
                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                                    : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                                }`}
                                title={user.isVendorVerified ? 'Click to unverify vendor' : 'Click to verify vendor'}
                              >
                                <BadgeCheck size={14} className={user.isVendorVerified ? 'text-emerald-600' : 'text-slate-500'} />
                                {user.isVendorVerified ? 'Verified' : 'Unverified'}
                              </button>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1.5 text-sm">
                              <span className="font-medium text-purple-700">{user.credits?.imageCredits ?? 0}</span>
                              <span className="text-gray-400">/</span>
                              <span className="font-medium text-purple-700">{user.credits?.videoCredits ?? 0}</span>
                              <span className="text-xs text-gray-500">img/vid</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm">
                              <div className="flex items-center gap-1.5">
                                <span className="font-medium text-purple-700">{user.consumption?.imageCredits ?? 0}</span>
                                <span className="text-gray-400">/</span>
                                <span className="font-medium text-purple-700">{user.consumption?.videoCredits ?? 0}</span>
                                <span className="text-xs text-gray-500">img/vid</span>
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                Total {user.consumption?.total ?? 0}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1.5 text-sm text-gray-700 whitespace-nowrap">
                              <Layers size={14} className="text-purple-400 flex-shrink-0" />
                              <span className="font-medium text-purple-700">{user.fabricsCount ?? 0}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1.5 text-sm text-gray-700 whitespace-nowrap">
                              <Sparkles size={14} className="text-purple-400 flex-shrink-0" />
                              <span className="font-medium text-purple-700">{user.aiGenerationsCount ?? user.consumption?.creationsCount ?? 0}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1.5 text-sm text-gray-700 whitespace-nowrap">
                              <Clock size={14} className="text-purple-400 flex-shrink-0" />
                              {formatAdminDateTime(user.lastUsedAt)}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${
                              user.settings?.virtualTryOnEnabled 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              <Zap size={12} className="flex-shrink-0" />
                              {user.settings?.virtualTryOnEnabled ? 'On' : 'Off'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() => handleExportUserContacts(user)}
                                disabled={exportingContactsUserId === user._id}
                                className="p-2 rounded-lg text-emerald-600 hover:bg-emerald-50 hover:text-emerald-800 transition-colors disabled:opacity-50"
                                title="Export contacts CSV"
                              >
                                {exportingContactsUserId === user._id ? (
                                  <Loader2 size={18} className="animate-spin" />
                                ) : (
                                  <Download size={18} />
                                )}
                              </button>
                              <button
                                type="button"
                                onClick={() => handleResendWelcomeEmail(user)}
                                disabled={!user.email || welcomeEmailUserId === user._id}
                                className="p-2 rounded-lg text-sky-600 hover:bg-sky-50 hover:text-sky-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                title={user.email ? `Resend welcome email to ${user.email}` : 'No email on this account'}
                              >
                                {welcomeEmailUserId === user._id ? (
                                  <Loader2 size={18} className="animate-spin" />
                                ) : (
                                  <Mail size={18} />
                                )}
                              </button>
                              <button
                                onClick={() => openUserSettingsModal(user)}
                                className="p-2 rounded-lg text-purple-600 hover:bg-purple-100 hover:text-purple-800 transition-colors"
                                title="User settings"
                              >
                                <Settings size={18} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {filteredUsersList.length > USERS_PAGE_SIZE && (
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 py-3 border-t border-purple-100 bg-purple-50/40">
                    <p className="text-sm text-gray-600">
                      Page {safeUsersPage} of {usersTotalPages}
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setUsersPage((page) => Math.max(1, page - 1))}
                        disabled={safeUsersPage <= 1}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-purple-700 bg-white border border-purple-200 rounded-lg hover:bg-purple-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft size={16} />
                        Previous
                      </button>
                      <button
                        type="button"
                        onClick={() => setUsersPage((page) => Math.min(usersTotalPages, page + 1))}
                        disabled={safeUsersPage >= usersTotalPages}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-purple-700 bg-white border border-purple-200 rounded-lg hover:bg-purple-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                        <ChevronRight size={16} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      case 'userSettings':
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-purple-700">User Settings Management</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl shadow-md border border-purple-100 p-5 sm:p-6">
                <div className="text-center py-8">
                  <Settings size={48} className="mx-auto text-pink-200 mb-4" />
                  <h3 className="text-lg font-semibold text-purple-700 mb-2">Manage User Settings</h3>
                  <p className="text-gray-600 mb-6">View and update user settings including virtual try-on permissions, notifications, and preferences.</p>
                  <button
                    onClick={() => setActiveSubMenu('users')}
                    className="px-6 py-3 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors font-semibold"
                  >
                    View Users
                  </button>
                </div>
              </div>
              
              <div className="bg-white rounded-xl shadow-md border border-purple-100 p-5 sm:p-6">
                <div className="text-center py-8">
                  <Zap size={48} className="mx-auto text-purple-200 mb-4" />
                  <h3 className="text-lg font-semibold text-purple-700 mb-2">Credit Management</h3>
                  <p className="text-gray-600 mb-6">Manage user credits for image and video generation. Monitor usage and allocate credits as needed.</p>
                  <div className="space-y-3">
                    <div className="bg-purple-50 rounded-lg p-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-purple-700">Total Image Credits</span>
                        <span className="text-lg font-bold text-purple-600">
                          {users.reduce((total, user) => total + (user.credits?.imageCredits || 0), 0)}
                        </span>
                      </div>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-purple-700">Total Video Credits</span>
                        <span className="text-lg font-bold text-purple-600">
                          {users.reduce((total, user) => total + (user.credits?.videoCredits || 0), 0)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setActiveSubMenu('users')}
                    className="mt-4 px-6 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors font-semibold"
                  >
                    Manage Credits
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      case 'communication':
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-purple-700 flex items-center gap-2">
              <MessageSquare size={28} />
              Communication
            </h2>
            <p className="text-gray-600">Send push notifications via Firebase to all users or selected users. Messages also appear in the in-app notification inbox.</p>

            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setCommunicationMode('broadcast')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                  communicationMode === 'broadcast'
                    ? 'bg-pink-500 text-white'
                    : 'bg-white border border-purple-200 text-purple-700 hover:bg-purple-50'
                }`}
              >
                <Send size={18} />
                Broadcast to All
              </button>
              <button
                onClick={() => setCommunicationMode('individual')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                  communicationMode === 'individual'
                    ? 'bg-pink-500 text-white'
                    : 'bg-white border border-purple-200 text-purple-700 hover:bg-purple-50'
                }`}
              >
                <Users size={18} />
                Individual Users
              </button>
            </div>

            <div className="bg-white rounded-xl shadow-lg border border-purple-100 p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-purple-700 mb-1">Title</label>
                <input
                  type="text"
                  value={commTitle}
                  onChange={(e) => setCommTitle(e.target.value)}
                  placeholder="e.g., New Feature Available"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-purple-700 mb-1">Message</label>
                <textarea
                  value={commMessage}
                  onChange={(e) => setCommMessage(e.target.value)}
                  placeholder="Enter your notification message..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {communicationMode === 'individual' && (
                <div>
                  <label className="block text-sm font-medium text-purple-700 mb-2">
                    Select Users ({selectedUserIds.length} selected)
                  </label>
                  <div className="relative mb-2">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={userSearchQuery}
                      onChange={(e) => setUserSearchQuery(e.target.value)}
                      placeholder="Search by name or phone..."
                      className="w-full pl-9 pr-3 py-2 border border-purple-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                    />
                    {userSearchQuery && (
                      <button
                        type="button"
                        onClick={() => setUserSearchQuery('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                  <div className="max-h-48 overflow-y-auto border border-purple-100 rounded-lg p-2 space-y-1">
                    {isLoadingUsers ? (
                      <div className="flex justify-center py-4">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
                      </div>
                    ) : (() => {
                      const nonAdminUsers = users.filter(u => (u.role || '').toLowerCase() !== 'admin');
                      const q = userSearchQuery.trim();
                      if (!q) {
                        if (nonAdminUsers.length === 0) {
                          return <p className="text-gray-500 text-sm py-2">No users to select</p>;
                        }
                        return nonAdminUsers.map(user => (
                          <label
                            key={user._id}
                            className="flex items-center gap-2 p-2 hover:bg-purple-50 rounded cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedUserIds.includes(user._id)}
                              onChange={() => toggleUserSelection(user._id)}
                              className="rounded text-pink-500 focus:ring-pink-500"
                            />
                            <span className="text-sm text-gray-700">{user.name}</span>
                            <span className="text-xs text-gray-500">({user.phone || user.email || 'N/A'})</span>
                          </label>
                        ));
                      }
                      const qLower = q.toLowerCase();
                      const qDigitsOnly = q.replace(/\D/g, '');
                      const filteredUsers = nonAdminUsers.filter(u => {
                        const name = String(u.name || '').toLowerCase();
                        const email = String(u.email || '').toLowerCase();
                        const phone = String(u.phone || '').replace(/\D/g, '');
                        const matchesName = name.includes(qLower);
                        const matchesEmail = qLower.length > 0 && email.includes(qLower);
                        const matchesPhone = qDigitsOnly.length > 0 && phone.length > 0 && phone.includes(qDigitsOnly);
                        return matchesName || matchesEmail || matchesPhone;
                      });
                      return filteredUsers.length === 0 ? (
                        <p className="text-gray-500 text-sm py-2">
                          {q ? 'No users match your search' : 'No users to select'}
                        </p>
                      ) : (
                        filteredUsers.map(user => (
                          <label
                            key={user._id}
                            className="flex items-center gap-2 p-2 hover:bg-purple-50 rounded cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedUserIds.includes(user._id)}
                              onChange={() => toggleUserSelection(user._id)}
                              className="rounded text-pink-500 focus:ring-pink-500"
                            />
                            <span className="text-sm text-gray-700">{user.name}</span>
                            <span className="text-xs text-gray-500">({user.phone || user.email || 'N/A'})</span>
                          </label>
                        ))
                      );
                    })()}
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedUserIds(users.filter(u => u.role !== 'admin').map(u => u._id))}
                    className="mt-2 text-sm text-purple-600 hover:text-purple-800"
                  >
                    Select all users
                  </button>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  onClick={communicationMode === 'broadcast' ? handleSendBroadcast : handleSendToUsers}
                  disabled={isSendingNotification}
                  className="px-6 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {isSendingNotification ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      {communicationMode === 'broadcast' ? 'Broadcast to All' : `Send to ${selectedUserIds.length} Users`}
                    </>
                  )}
                </button>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm text-purple-700">
                <strong>How it works:</strong> Notifications are sent via Firebase Cloud Messaging (push) to users who have enabled notifications. All recipients also receive an in-app notification in their notification inbox.
              </div>
            </div>
          </div>
        );
      case 'whatsapp':
        return (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold text-purple-700 flex items-center gap-2">
                  <MessageCircle size={28} className="text-green-600" />
                  WhatsApp (AiSensy)
                </h2>
                <p className="text-gray-600 mt-1">
                  Send approved WhatsApp template messages to all users or selected users via AiSensy API campaigns.
                </p>
              </div>
              <button
                onClick={fetchWhatsappData}
                disabled={isLoadingWhatsapp}
                className="px-4 py-2 bg-white border border-purple-200 text-purple-700 rounded-lg hover:bg-purple-50 transition-colors flex items-center gap-2 font-medium disabled:opacity-50 self-start"
              >
                <RefreshCw size={16} className={isLoadingWhatsapp ? 'animate-spin' : ''} />
                Refresh
              </button>
            </div>

            {!whatsappConfigured && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800 text-sm">
                <strong>Not configured:</strong> Set <code className="bg-amber-100 px-1 rounded">AISENSY_API_KEY</code> in backend environment.
                Create live API campaigns in your AiSensy dashboard and map them to templates below.
              </div>
            )}

            <div className="flex gap-2">
              <button
                onClick={() => setWhatsappTab('send')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                  whatsappTab === 'send'
                    ? 'bg-green-600 text-white'
                    : 'bg-white border border-purple-200 text-purple-700 hover:bg-purple-50'
                }`}
              >
                <Send size={18} />
                Send Messages
              </button>
              <button
                onClick={() => setWhatsappTab('templates')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                  whatsappTab === 'templates'
                    ? 'bg-green-600 text-white'
                    : 'bg-white border border-purple-200 text-purple-700 hover:bg-purple-50'
                }`}
              >
                <FileText size={18} />
                Templates
              </button>
            </div>

            {whatsappTab === 'send' && (
              <div className="bg-white rounded-xl shadow-lg border border-purple-100 p-6 space-y-6">
                <div className="flex gap-2">
                  <button
                    onClick={() => setWhatsappMode('broadcast')}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                      whatsappMode === 'broadcast'
                        ? 'bg-pink-500 text-white'
                        : 'bg-white border border-purple-200 text-purple-700 hover:bg-purple-50'
                    }`}
                  >
                    <Send size={18} />
                    Broadcast to All
                  </button>
                  <button
                    onClick={() => setWhatsappMode('individual')}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                      whatsappMode === 'individual'
                        ? 'bg-pink-500 text-white'
                        : 'bg-white border border-purple-200 text-purple-700 hover:bg-purple-50'
                    }`}
                  >
                    <Users size={18} />
                    Selected Users
                  </button>
                </div>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={whatsappUseCustom}
                    onChange={(e) => {
                      setWhatsappUseCustom(e.target.checked);
                      if (e.target.checked && whatsappParamValues.length === 0) {
                        setWhatsappParamValues(['']);
                      }
                    }}
                    className="rounded text-green-600 focus:ring-green-500"
                  />
                  <span className="text-sm text-gray-700">Use custom campaign (enter AiSensy campaign name directly)</span>
                </label>

                {whatsappUseCustom ? (
                  <div>
                    <label className="block text-sm font-medium text-purple-700 mb-1">AiSensy Campaign Name</label>
                    <input
                      type="text"
                      value={whatsappCustomCampaign}
                      onChange={(e) => setWhatsappCustomCampaign(e.target.value)}
                      placeholder="Exact campaign name from AiSensy dashboard"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-purple-700 mb-1">Select Template</label>
                    <select
                      value={selectedWhatsappTemplateId}
                      onChange={(e) => setSelectedWhatsappTemplateId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <option value="">Choose a saved template...</option>
                      {whatsappTemplates.filter((t) => t.isActive !== false).map((t) => (
                        <option key={t._id} value={t._id}>{t.name} ({t.campaignName})</option>
                      ))}
                    </select>
                    {selectedWhatsappTemplate?.previewText && (
                      <p className="mt-2 text-sm text-gray-500 bg-gray-50 p-2 rounded border">
                        Preview: {selectedWhatsappTemplate.previewText}
                      </p>
                    )}
                  </div>
                )}

                {(selectedWhatsappTemplate?.paramLabels?.length > 0 || whatsappUseCustom) && (
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-purple-700">Template Parameters</label>
                    {(whatsappUseCustom
                      ? whatsappParamValues
                      : (selectedWhatsappTemplate?.paramLabels || [])
                    ).map((label, idx) => (
                      <div key={idx}>
                        <label className="block text-xs text-gray-500 mb-1">
                          {whatsappUseCustom ? `Parameter ${idx + 1}` : (label || `Parameter ${idx + 1}`)}
                        </label>
                        <input
                          type="text"
                          value={whatsappParamValues[idx] || ''}
                          onChange={(e) => {
                            const next = [...whatsappParamValues];
                            next[idx] = e.target.value;
                            setWhatsappParamValues(next);
                          }}
                          placeholder={`Value for ${whatsappUseCustom ? `param ${idx + 1}` : label}`}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        />
                      </div>
                    ))}
                    {whatsappUseCustom && (
                      <button
                        type="button"
                        onClick={() => setWhatsappParamValues([...whatsappParamValues, ''])}
                        className="text-sm text-purple-600 hover:text-purple-800 flex items-center gap-1"
                      >
                        <Plus size={14} /> Add parameter
                      </button>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-purple-700 mb-1">Media URL (optional)</label>
                    <input
                      type="url"
                      value={whatsappMediaUrl}
                      onChange={(e) => setWhatsappMediaUrl(e.target.value)}
                      placeholder="https://..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-purple-700 mb-1">Media Filename (optional)</label>
                    <input
                      type="text"
                      value={whatsappMediaFilename}
                      onChange={(e) => setWhatsappMediaFilename(e.target.value)}
                      placeholder="image.jpg"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                </div>

                {whatsappMode === 'individual' && (
                  <div>
                    <label className="block text-sm font-medium text-purple-700 mb-2">
                      Select Users ({selectedWhatsappUserIds.length} selected)
                    </label>
                    <div className="relative mb-2">
                      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        value={whatsappUserSearch}
                        onChange={(e) => setWhatsappUserSearch(e.target.value)}
                        placeholder="Search by name or phone..."
                        className="w-full pl-9 pr-3 py-2 border border-purple-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                      />
                    </div>
                    <div className="max-h-48 overflow-y-auto border border-purple-100 rounded-lg p-2 space-y-1">
                      {(() => {
                        const nonAdminUsers = users.filter((u) => (u.role || '').toLowerCase() !== 'admin' && u.phone);
                        const q = whatsappUserSearch.trim().toLowerCase();
                        const qDigits = q.replace(/\D/g, '');
                        const filtered = nonAdminUsers.filter((u) => {
                          if (!q) return true;
                          const name = String(u.name || '').toLowerCase();
                          const phone = String(u.phone || '').replace(/\D/g, '');
                          return name.includes(q) || (qDigits && phone.includes(qDigits));
                        });
                        if (filtered.length === 0) {
                          return <p className="text-gray-500 text-sm py-2">No users with phone numbers found</p>;
                        }
                        return filtered.map((user) => (
                          <label key={user._id} className="flex items-center gap-2 p-2 hover:bg-purple-50 rounded cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedWhatsappUserIds.includes(user._id)}
                              onChange={() => toggleWhatsappUserSelection(user._id)}
                              className="rounded text-green-600 focus:ring-green-500"
                            />
                            <span className="text-sm text-gray-700">{user.name}</span>
                            <span className="text-xs text-gray-500">({user.phone})</span>
                          </label>
                        ));
                      })()}
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedWhatsappUserIds(users.filter((u) => u.role !== 'admin' && u.phone).map((u) => u._id))}
                      className="mt-2 text-sm text-purple-600 hover:text-purple-800"
                    >
                      Select all users with phone
                    </button>
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={whatsappMode === 'broadcast' ? handleWhatsappBroadcast : handleWhatsappSendToUsers}
                    disabled={isSendingWhatsapp || !whatsappConfigured}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    {isSendingWhatsapp ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send size={18} />
                        {whatsappMode === 'broadcast' ? 'Broadcast WhatsApp' : `Send to ${selectedWhatsappUserIds.length} Users`}
                      </>
                    )}
                  </button>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
                  <strong>Setup:</strong> Create an API Campaign in AiSensy with an approved WhatsApp template, set it live, then save the campaign name here as a template. Only users with phone numbers on file will receive messages.
                </div>

                {whatsappBroadcasts.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-purple-700 mb-2">Recent Broadcasts</h3>
                    <div className="overflow-x-auto border border-purple-100 rounded-lg">
                      <table className="min-w-full text-sm">
                        <thead className="bg-purple-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-purple-700">Date</th>
                            <th className="px-3 py-2 text-left text-purple-700">Campaign</th>
                            <th className="px-3 py-2 text-left text-purple-700">Mode</th>
                            <th className="px-3 py-2 text-left text-purple-700">Sent</th>
                            <th className="px-3 py-2 text-left text-purple-700">Failed</th>
                            <th className="px-3 py-2 text-left text-purple-700">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {whatsappBroadcasts.slice(0, 10).map((b) => (
                            <tr key={b._id} className="border-t border-purple-50">
                              <td className="px-3 py-2 text-gray-600">{new Date(b.createdAt).toLocaleString()}</td>
                              <td className="px-3 py-2">{b.campaignName}</td>
                              <td className="px-3 py-2 capitalize">{b.mode}</td>
                              <td className="px-3 py-2 text-green-700">{b.successCount}</td>
                              <td className="px-3 py-2 text-red-600">{b.failureCount}</td>
                              <td className="px-3 py-2 capitalize">{b.status}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {whatsappTab === 'templates' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <p className="text-gray-600 text-sm">Manage WhatsApp message templates linked to AiSensy API campaigns.</p>
                  <button
                    onClick={() => openTemplateModal()}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 text-sm font-medium"
                  >
                    <Plus size={16} />
                    New Template
                  </button>
                </div>

                {isLoadingWhatsapp ? (
                  <div className="flex justify-center py-12">
                    <Loader2 size={32} className="animate-spin text-green-600" />
                  </div>
                ) : whatsappTemplates.length === 0 ? (
                  <div className="bg-white rounded-xl border border-purple-100 p-8 text-center text-gray-500">
                    No templates yet. Create one to get started.
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {whatsappTemplates.map((t) => (
                      <div key={t._id} className="bg-white rounded-xl border border-purple-100 p-5 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-purple-800">{t.name}</h3>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${t.isActive !== false ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                              {t.isActive !== false ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500">Campaign: <code className="bg-gray-100 px-1 rounded">{t.campaignName}</code></p>
                          {t.description && <p className="text-sm text-gray-600 mt-1">{t.description}</p>}
                          {t.previewText && (
                            <p className="text-sm text-gray-500 mt-2 bg-gray-50 p-2 rounded border">{t.previewText}</p>
                          )}
                          {t.paramLabels?.length > 0 && (
                            <p className="text-xs text-gray-400 mt-2">
                              Params: {t.paramLabels.join(', ')}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2 shrink-0">
                          <button
                            onClick={() => openTemplateModal(t)}
                            className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg"
                            title="Edit"
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            onClick={() => handleDeleteTemplate(t._id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                            title="Delete"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      case 'vendorConnectRequests':
        return (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <h2 className="text-2xl font-bold text-purple-700">Vendor Connect Requests</h2>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={fetchVendorConnectRequests}
                  className="px-4 py-2 bg-white border border-purple-200 text-purple-700 rounded-lg hover:bg-purple-50 transition-colors flex items-center gap-2 font-medium disabled:opacity-50"
                  disabled={isLoadingVendorConnectRequests}
                >
                  <RefreshCw size={16} className={isLoadingVendorConnectRequests ? 'animate-spin' : ''} />
                  {isLoadingVendorConnectRequests ? 'Loading...' : 'Refresh'}
                </button>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md border border-purple-100 p-4">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={vendorConnectSearch}
                  onChange={(e) => setVendorConnectSearch(e.target.value)}
                  placeholder="Search by requester, mobile, fabric, vendor..."
                  className="w-full pl-9 pr-9 py-2 border border-purple-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                />
                {vendorConnectSearch && (
                  <button
                    type="button"
                    onClick={() => setVendorConnectSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label="Clear search"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Showing {filteredVendorConnectRequests.length} of {vendorConnectRequests.length}
              </p>
            </div>

            {isLoadingVendorConnectRequests ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
              </div>
            ) : filteredVendorConnectRequests.length === 0 ? (
              <div className="bg-white rounded-xl shadow-md border border-purple-100 p-8 text-center">
                <MessageCircle size={48} className="mx-auto text-pink-200 mb-4" />
                <p className="text-gray-600">No vendor connect requests found.</p>
                <button
                  onClick={fetchVendorConnectRequests}
                  className="mt-4 px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors"
                >
                  Load Requests
                </button>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-md border border-purple-100 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-purple-100">
                    <thead className="bg-gradient-to-r from-pink-50 to-purple-50 sticky top-0 z-10">
                      <tr>
                        <th className="px-4 py-3.5 text-left text-xs font-semibold text-purple-700 uppercase tracking-wider">Requested</th>
                        <th className="px-4 py-3.5 text-left text-xs font-semibold text-purple-700 uppercase tracking-wider">Requester</th>
                        <th className="px-4 py-3.5 text-left text-xs font-semibold text-purple-700 uppercase tracking-wider">Mobile</th>
                        <th className="px-4 py-3.5 text-left text-xs font-semibold text-purple-700 uppercase tracking-wider">Fabric</th>
                        <th className="px-4 py-3.5 text-left text-xs font-semibold text-purple-700 uppercase tracking-wider">Vendor</th>
                        <th className="px-4 py-3.5 text-left text-xs font-semibold text-purple-700 uppercase tracking-wider">IDs</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-purple-50">
                      {filteredVendorConnectRequests.map((r) => {
                        const when = r.createdAt ? new Date(r.createdAt) : null;
                        const requester = r.requesterName || '—';
                        const mobile = r.mobileNumber || '—';
                        const fabricName = r.fabricId?.name || '—';
                        const vendorName = r.vendorId?.name || '—';
                        const vendorPhone = r.vendorId?.phone || '';
                        const fabricId = r.fabricId?._id || r.fabricId || '';
                        const vendorId = r.vendorId?._id || r.vendorId || '';
                        return (
                          <tr key={r._id} className="hover:bg-purple-50/50 transition-colors">
                            <td className="px-4 py-3">
                              <div className="text-sm text-gray-700">
                                {when ? when.toLocaleString() : '—'}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-sm font-medium text-purple-900 truncate max-w-[200px]" title={requester}>
                                {requester}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-sm text-gray-700">{mobile}</div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-sm text-gray-800 truncate max-w-[260px]" title={fabricName}>
                                {fabricName}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-sm text-gray-800 truncate max-w-[220px]" title={vendorName}>
                                {vendorName}
                              </div>
                              {vendorPhone && (
                                <div className="text-xs text-gray-500">{vendorPhone}</div>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-xs text-gray-600 space-y-1">
                                <div className="truncate max-w-[260px]" title={String(fabricId)}>
                                  <span className="font-semibold">Fabric:</span> {String(fabricId)}
                                </div>
                                <div className="truncate max-w-[260px]" title={String(vendorId)}>
                                  <span className="font-semibold">Vendor:</span> {String(vendorId)}
                                </div>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );
      case 'supportRequests':
        return (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold text-purple-700">Support Requests</h2>
                <p className="text-sm text-gray-600 mt-1">Callback requests and help messages submitted across the app.</p>
              </div>
              <button
                onClick={fetchSupportRequests}
                className="px-4 py-2 rounded-lg bg-purple-600 text-white font-semibold hover:bg-purple-700 transition-colors inline-flex items-center gap-2"
                disabled={supportLoading}
              >
                <RefreshCw size={16} className={supportLoading ? 'animate-spin' : ''} />
                Refresh
              </button>
            </div>

            <div className="bg-white rounded-xl shadow-md border border-purple-100 p-4">
              <div className="flex flex-col md:flex-row gap-3 md:items-center">
                <div className="flex-1">
                  <label className="text-xs font-semibold text-gray-600">Search</label>
                  <input
                    value={supportSearch}
                    onChange={(e) => setSupportSearch(e.target.value)}
                    placeholder="Phone, message, or source…"
                    className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-200"
                  />
                </div>
                <div className="w-full md:w-56">
                  <label className="text-xs font-semibold text-gray-600">Status</label>
                  <select
                    value={supportStatusFilter}
                    onChange={(e) => setSupportStatusFilter(e.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-200"
                  >
                    <option value="">All</option>
                    <option value="open">Open</option>
                    <option value="in_progress">In progress</option>
                    <option value="resolved">Resolved</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md border border-purple-100 overflow-hidden">
              {supportLoading ? (
                <div className="p-10 text-center text-gray-600">
                  <Loader2 size={28} className="mx-auto animate-spin text-purple-500 mb-3" />
                  Loading support requests…
                </div>
              ) : supportRequests.length === 0 ? (
                <div className="p-10 text-center text-gray-600">No support requests yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-purple-50">
                    <thead className="bg-purple-50/60">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-purple-700">Created</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-purple-700">Phone</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-purple-700">Callback</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-purple-700">Source</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-purple-700">Message</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-purple-700">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-purple-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-purple-50">
                      {supportRequests.map((r) => {
                        const when = r.createdAt ? new Date(r.createdAt) : null;
                        const phone = r.phone || '—';
                        const msg = r.message || '';
                        const source = r.source || '—';
                        const status = r.status || 'open';
                        return (
                          <tr key={r._id} className="hover:bg-purple-50/40 transition-colors">
                            <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                              {when ? when.toLocaleString() : '—'}
                            </td>
                            <td className="px-4 py-3 text-sm font-semibold text-gray-900 whitespace-nowrap">{phone}</td>
                            <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                              {r.requestCallback ? 'Yes' : 'No'}
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-600 max-w-[220px] truncate" title={source}>
                              {source}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 max-w-[520px] truncate" title={msg}>
                              {msg || '—'}
                            </td>
                            <td className="px-4 py-3 text-sm whitespace-nowrap">
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                  status === 'resolved'
                                    ? 'bg-green-100 text-green-700'
                                    : status === 'in_progress'
                                      ? 'bg-yellow-100 text-yellow-800'
                                      : 'bg-blue-100 text-blue-700'
                                }`}
                              >
                                {status.replace('_', ' ')}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={async () => {
                                    try {
                                      const token = localStorage.getItem('token');
                                      await updateSupportRequest(r._id, { status: 'in_progress' }, token);
                                      setSupportRequests((prev) =>
                                        prev.map((x) => (x._id === r._id ? { ...x, status: 'in_progress' } : x))
                                      );
                                      showNotification('Marked in progress', 'success');
                                    } catch (e) {
                                      showNotification(e?.response?.data?.message || 'Failed to update', 'error');
                                    }
                                  }}
                                  className="px-3 py-1.5 rounded-lg bg-yellow-500 text-white text-xs font-semibold hover:bg-yellow-600 transition-colors"
                                  disabled={status === 'in_progress'}
                                >
                                  In progress
                                </button>
                                <button
                                  onClick={async () => {
                                    try {
                                      const token = localStorage.getItem('token');
                                      await updateSupportRequest(r._id, { status: 'resolved' }, token);
                                      setSupportRequests((prev) =>
                                        prev.map((x) => (x._id === r._id ? { ...x, status: 'resolved' } : x))
                                      );
                                      showNotification('Marked resolved', 'success');
                                    } catch (e) {
                                      showNotification(e?.response?.data?.message || 'Failed to update', 'error');
                                    }
                                  }}
                                  className="px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-semibold hover:bg-green-700 transition-colors"
                                  disabled={status === 'resolved'}
                                >
                                  Resolve
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        );
      case 'feedback':
        return (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold text-purple-700">User Feedback</h2>
                <p className="text-sm text-gray-600 mt-1">Star ratings and comments submitted by users in-app.</p>
              </div>
              <button
                onClick={fetchFeedback}
                className="px-4 py-2 rounded-lg bg-purple-600 text-white font-semibold hover:bg-purple-700 transition-colors inline-flex items-center gap-2"
                disabled={feedbackLoading}
              >
                <RefreshCw size={16} className={feedbackLoading ? 'animate-spin' : ''} />
                Refresh
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-white rounded-xl shadow-md border border-purple-100 p-5 flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-yellow-50 flex items-center justify-center">
                  <Star size={22} className="text-yellow-400 fill-yellow-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {feedbackStats.averageRating || 0}
                    <span className="text-base text-gray-400 font-semibold"> / 5</span>
                  </div>
                  <div className="text-xs font-semibold text-gray-500">Average rating</div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-md border border-purple-100 p-5 flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-purple-50 flex items-center justify-center">
                  <MessageSquare size={22} className="text-purple-500" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">{feedbackStats.totalCount || 0}</div>
                  <div className="text-xs font-semibold text-gray-500">Total responses</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md border border-purple-100 p-4">
              <div className="flex flex-col md:flex-row gap-3 md:items-center">
                <div className="flex-1">
                  <label className="text-xs font-semibold text-gray-600">Search</label>
                  <input
                    value={feedbackSearch}
                    onChange={(e) => setFeedbackSearch(e.target.value)}
                    placeholder="Comment or source…"
                    className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-200"
                  />
                </div>
                <div className="w-full md:w-56">
                  <label className="text-xs font-semibold text-gray-600">Rating</label>
                  <select
                    value={feedbackRatingFilter}
                    onChange={(e) => setFeedbackRatingFilter(e.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-200"
                  >
                    <option value="">All ratings</option>
                    <option value="5">5 stars</option>
                    <option value="4">4 stars</option>
                    <option value="3">3 stars</option>
                    <option value="2">2 stars</option>
                    <option value="1">1 star</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md border border-purple-100 overflow-hidden">
              {feedbackLoading ? (
                <div className="p-10 text-center text-gray-600">
                  <Loader2 size={28} className="mx-auto animate-spin text-purple-500 mb-3" />
                  Loading feedback…
                </div>
              ) : feedback.length === 0 ? (
                <div className="p-10 text-center text-gray-600">No feedback yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-purple-50">
                    <thead className="bg-purple-50/60">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-purple-700">Created</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-purple-700">User</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-purple-700">Rating</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-purple-700">Comment</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-purple-700">Source</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-purple-50">
                      {feedback.map((f) => {
                        const when = f.createdAt ? new Date(f.createdAt) : null;
                        const userName = f.user?.name || 'Unknown';
                        const userContact = f.user?.email || f.user?.phone || '';
                        const stars = Math.max(0, Math.min(5, Number(f.rating) || 0));
                        return (
                          <tr key={f._id} className="hover:bg-purple-50/40 transition-colors">
                            <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                              {when ? when.toLocaleString() : '—'}
                            </td>
                            <td className="px-4 py-3 text-sm whitespace-nowrap">
                              <div className="font-semibold text-gray-900">{userName}</div>
                              {userContact ? <div className="text-xs text-gray-500">{userContact}</div> : null}
                            </td>
                            <td className="px-4 py-3 text-sm whitespace-nowrap">
                              <div className="flex items-center gap-0.5">
                                {[1, 2, 3, 4, 5].map((v) => (
                                  <Star
                                    key={v}
                                    size={16}
                                    className={v <= stars ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}
                                  />
                                ))}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 max-w-[520px]" title={f.message || ''}>
                              {f.message || '—'}
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-600 max-w-[200px] truncate" title={f.source || ''}>
                              {f.source || '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex min-h-screen">
        {/* Sub-menu Sidebar */}
        <aside className="flex-shrink-0 w-64 min-w-[16rem] bg-white border-r border-purple-100 shadow-sm">
          <div className="sticky top-0 p-4">
            <h2 className="text-xl font-bold text-purple-700 mb-6">
              Admin Panel
            </h2>
            <nav className="space-y-1">
              {subMenus.map(menu => {
                const Icon = menu.icon;
                return (
                  <button
                    key={menu.id}
                    onClick={() => setActiveSubMenu(menu.id)}
                    className={`w-full flex items-center px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      activeSubMenu === menu.id
                        ? 'bg-pink-500 text-white shadow-sm'
                        : 'text-purple-700 hover:bg-purple-50'
                    }`}
                  >
                    <Icon size={20} className="mr-3 flex-shrink-0" />
                    <span className="truncate">{menu.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-w-0 overflow-auto">
          <div className="p-6 sm:p-8 max-w-[1600px]">
            {renderContent()}
          </div>
        </main>
      </div>

      {/* Notification */}
      {notification && (
        <div className={`fixed top-5 right-5 p-4 rounded-lg shadow-lg text-white z-[100] flex items-center ${
          notification.type === 'success' ? 'bg-green-500' : 
          notification.type === 'error' ? 'bg-red-500' : 
          'bg-blue-500'
        }`}>
          {notification.message}
          <button onClick={() => setNotification(null)} className="ml-4 text-white hover:text-gray-200">
            <X size={18} />
          </button>
        </div>
      )}

      {/* Approval Modal */}
      {isApprovalModalOpen && selectedKit && (
        <ApprovalModal
          kit={selectedKit}
          onClose={() => {
            setIsApprovalModalOpen(false);
            setSelectedKit(null);
          }}
          onApprove={handleApprove}
          showNotification={showNotification}
        />
      )}

      {/* Add User Modal */}
      {isAddUserModalOpen && (
        <AddUserModal
          onClose={() => setIsAddUserModalOpen(false)}
          onAddUser={handleAddUser}
          showNotification={showNotification}
        />
      )}

      {/* User Settings Modal */}
      {isUserSettingsModalOpen && selectedUser && (
        <UserSettingsModal
          user={selectedUser}
          onClose={() => {
            setIsUserSettingsModalOpen(false);
            setSelectedUser(null);
          }}
          onUpdate={updateUserSettings}
          onExportContacts={handleExportUserContacts}
          isExportingContacts={exportingContactsUserId === selectedUser._id}
          showNotification={showNotification}
        />
      )}

      {/* Edit Kit Modal */}
      <EditKitModal
        kit={kitToEdit}
        isOpen={isEditKitModalOpen}
        onClose={() => {
          setIsEditKitModalOpen(false);
          setKitToEdit(null);
        }}
        onUpdate={handleUpdateKit}
        showNotification={showNotification}
      />

      {/* WhatsApp Template Modal */}
      {isTemplateModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-bold text-purple-700">
                {editingTemplate ? 'Edit Template' : 'New WhatsApp Template'}
              </h3>
              <button onClick={() => setIsTemplateModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-purple-700 mb-1">Template Name</label>
              <input
                type="text"
                value={templateForm.name}
                onChange={(e) => setTemplateForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Promotional Offer"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-purple-700 mb-1">AiSensy Campaign Name</label>
              <input
                type="text"
                value={templateForm.campaignName}
                onChange={(e) => setTemplateForm((p) => ({ ...p, campaignName: e.target.value }))}
                placeholder="Exact name from AiSensy dashboard"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-purple-700 mb-1">Description</label>
              <input
                type="text"
                value={templateForm.description}
                onChange={(e) => setTemplateForm((p) => ({ ...p, description: e.target.value }))}
                placeholder="Internal note for admins"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-purple-700 mb-1">Message Preview</label>
              <textarea
                value={templateForm.previewText}
                onChange={(e) => setTemplateForm((p) => ({ ...p, previewText: e.target.value }))}
                placeholder="Hello {{1}}, check out {{2}}!"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-purple-700 mb-2">Template Parameters</label>
              {templateForm.paramLabels.map((label, idx) => (
                <div key={idx} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={label}
                    onChange={(e) => {
                      const next = [...templateForm.paramLabels];
                      next[idx] = e.target.value;
                      setTemplateForm((p) => ({ ...p, paramLabels: next }));
                    }}
                    placeholder={`Param label ${idx + 1}`}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                  <input
                    type="text"
                    value={templateForm.defaultParams[idx] || ''}
                    onChange={(e) => {
                      const next = [...templateForm.defaultParams];
                      next[idx] = e.target.value;
                      setTemplateForm((p) => ({ ...p, defaultParams: next }));
                    }}
                    placeholder="Default value"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                  {templateForm.paramLabels.length > 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        setTemplateForm((p) => ({
                          ...p,
                          paramLabels: p.paramLabels.filter((_, i) => i !== idx),
                          defaultParams: p.defaultParams.filter((_, i) => i !== idx)
                        }));
                      }}
                      className="p-2 text-red-500 hover:bg-red-50 rounded"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => setTemplateForm((p) => ({
                  ...p,
                  paramLabels: [...p.paramLabels, ''],
                  defaultParams: [...p.defaultParams, '']
                }))}
                className="text-sm text-purple-600 hover:text-purple-800 flex items-center gap-1"
              >
                <Plus size={14} /> Add parameter
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-purple-700 mb-1">Media URL</label>
                <input
                  type="url"
                  value={templateForm.mediaUrl}
                  onChange={(e) => setTemplateForm((p) => ({ ...p, mediaUrl: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-purple-700 mb-1">Media Filename</label>
                <input
                  type="text"
                  value={templateForm.mediaFilename}
                  onChange={(e) => setTemplateForm((p) => ({ ...p, mediaFilename: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={templateForm.isActive}
                onChange={(e) => setTemplateForm((p) => ({ ...p, isActive: e.target.checked }))}
                className="rounded text-green-600"
              />
              <span className="text-sm text-gray-700">Active</span>
            </label>

            <div className="flex gap-3 pt-2">
              <button
                onClick={handleSaveTemplate}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
              >
                {editingTemplate ? 'Update Template' : 'Create Template'}
              </button>
              <button
                onClick={() => setIsTemplateModalOpen(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanelView;